import { draftLevels, FollowUpTab, levelsError, ReminderSettings, savedLevels, type ReminderLevel } from './FollowUp.tsx';
import { CustomFieldInputs, CustomFieldsView, useCustomColumns, useCustomFields } from '../components/CustomFields.tsx';
import { TaxRateSelect } from '../components/TaxRateSelect.tsx';
import { idHint } from '../lib/country.ts';
import { PACK_GROUPS } from '../components/CountrySelect.tsx';
import { buyerIdLabel, invoiceLegalNotes, knownRateOr, standardRateNow, useTaxRates } from '../lib/taxes.ts';
import { countryName } from '../lib/business.ts';
import { CountrySelect } from '../components/CountrySelect.tsx';
import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { QrCode } from '../components/Receipt.tsx';
import { DeliveryDialog, DeliveryList } from './Deliveries.tsx';
import { StatementDialog } from './Statement.tsx';
import { CommissionsTab } from './Commissions.tsx';
import { PricelistsTab, QuoteBadge, QuoteDialog, QuoteList, quoteName, type QuoteDetail, type QuoteRow } from './Quotes.tsx';
import { action, ApiError, saveBlob } from '../lib/api.ts';
import { day, money, moneyIn, parseMoney, rateText, todayIso } from '../lib/format.ts';
import { CurrencyFields, effectiveRate, toBase, useCurrencies, useDayRate } from './manage/Currencies.tsx';
import { usePlaces } from '../lib/places.tsx';
import { DEFAULT_LOOK, LookPicker, PaperEnd, PaperFields, PaperLogoRight, paperAttrs, PaperSeller, useDocLook, type PaperLook } from '../lib/doclook.tsx';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { NewLink } from './Payments.tsx';
import { useLinkParam } from '../lib/link-params.ts';
import { PaymentTermsPanel, SchedulePreview, TermSelect, termSummary, usePaymentTerms, type ScheduledPayment } from '../components/PaymentTerms.tsx';
import { RecordNotes } from '../components/RecordNotes.tsx';

type Kind = 'invoice' | 'credit';
type State = 'draft' | 'issued' | 'paid' | 'cancelled';
type Method = 'bank' | 'cash' | 'card' | 'other';
export type Unit = (typeof UNITS)[number];
export const UNITS = ['pcs', 'kg', 'g', 'l', 'ml', 'm', 'm2', 'm3', 'h', 'day', 'box', 'set', 'service'] as const;
const METHODS: { key: Method; icon: IconName }[] = [
  { key: 'bank', icon: 'bank' },
  { key: 'cash', icon: 'cash' },
  { key: 'card', icon: 'card' },
  { key: 'other', icon: 'dots' },
];
const ID_TYPES = ['NUIS', 'ID', 'PASS', 'VAT', 'TAX', 'SOC'] as const;

interface Overview {
  today: string;
  drafts: number;
  open: number;
  overdue: number;
  overdue_count: number;
  month_net: number;
  quotes: number;
  quotes_total: number;
  orders: number;
}
interface InvoiceRow {
  id: string;
  kind: Kind;
  number: number | null;
  year: number | null;
  state: State;
  customer: string;
  customer_name?: string;
  date: string;
  due_date: string;
  reference: string | null;
  net?: number;
  tax?: number;
  total: number;
  paid: number;
  credit_of?: string | null;
  days_overdue: number;
  /** Another currency: its code and the document's own figures in it. */
  currency?: string | null;
  fx_total?: number | null;
  fx_paid?: number | null;
  /** Who sold it: their commission plan counts it. */
  salesperson?: string | null;
  /** The payment term; issued, its instalments and the early-payment discount. */
  payment_term?: string | null;
  schedule?: ScheduledPayment[] | null;
  discount_until?: string | null;
  discount_amount?: number | null;
  discount_percent?: number | null;
}
export interface Customer {
  id: string;
  name: string;
  is_company: boolean;
  tax_id: string | null;
  id_type: (typeof ID_TYPES)[number] | null;
  address: string | null;
  town: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  payment_days: number;
  payment_term?: string | null;
  credit_limit: number | null;
  note?: string | null;
  active: boolean;
  open?: number;
  overdue?: number;
  last_invoice?: string | null;
  invoices?: number;
  problems?: string[];
  pricelist?: string | null;
  pricelist_name?: string | null;
  /** The last check of the EU VAT number in VIES. */
  vies_state?: 'valid' | 'invalid' | null;
  vies_checked_at?: string | null;
  vies_name?: string | null;
  /** Where e-invoices reach them: a Peppol ID ("0088:…") or Italy's SDI code or PEC. */
  einvoice_id?: string | null;
}
export interface Line {
  id: string;
  product: string | null;
  name: string;
  unit: Unit;
  qty: number;
  unit_price: number;
  discount: number;
  tax_rate: number;
  net: number;
  tax: number;
  total: number;
  credited: number;
  order_line?: string | null;
  fx_unit_price?: number | null;
  fx_net?: number | null;
  fx_tax?: number | null;
  fx_total?: number | null;
}
interface BankAccount {
  bank: string;
  iban: string;
  swift: string | null;
  currency: string;
}
export interface Settings {
  payment_days: number;
  payment_term?: string | null;
  reminder_levels?: ReminderLevel[];
  reminders_auto?: boolean;
  quote_days: number;
  terms: string | null;
  bank_accounts: BankAccount[];
}
export interface Detail {
  invoice: InvoiceRow & {
    name: string | null;
    net: number;
    tax: number;
    location: string;
    location_name: string;
    note: string | null;
    reason: string | null;
    return_goods: boolean;
    cancel_reason: string | null;
    issued_at: string | null;
    issued_by_name: string | null;
    buyer: { name: string; tax_id: string | null; id_type: string | null; address: string | null; town: string | null; country: string } | null;
    sales_order: string | null;
    rate?: number | null;
    fx_net?: number | null;
    fx_tax?: number | null;
  };
  /** The currency the business keeps its books in. */
  base_currency?: string;
  customer: Customer & { balance: { open: number; overdue: number }; problems: string[] };
  lines: Line[];
  payments: {
    id: string;
    amount: number;
    method: Method | 'credit_note';
    reference: string | null;
    paid_on: string;
    by_name: string | null;
    other_kind: Kind | null;
    other_number: number | null;
    other_year: number | null;
    fx_amount?: number | null;
    rate?: number | null;
    fx_diff?: number | null;
  }[];
  related: { id: string; kind: Kind; number: number | null; year: number | null; state: State; date: string; total: number }[];
  company: { legal_name: string; trade_name: string | null; tax_id: string | null; address: string | null; city: string | null; country: string; phone: string | null; email: string | null; website: string | null };
  settings: Settings;
  branch: { name: string; address: string | null; city: string | null };
  /** The sales order the invoice was made from. */
  order: { id: string; number: number; year: number; reference: string | null } | null;
  /** Albania: the fiscal codes and the e-invoice, once fiscalization is on. */
  fiscal: Fiscal | null;
}
interface Fiscal {
  test: boolean;
  state: 'pending' | 'fiscalized' | 'rejected';
  last_error: string | null;
  inv_num: string;
  issued_at: string;
  iic: string;
  fic: string | null;
  operator: string;
  business_unit: string;
  tcr: string | null;
  software: string;
  qr: string[];
  corrects: string | null;
  einvoice: { state: 'waiting' | 'pending' | 'sent' | 'rejected'; eic: string | null; last_error: string | null; xml: string | null } | null;
}
export interface Sellable {
  id: string;
  name: string;
  sku: string | null;
  tax_rate: number;
  /** For the customer picked: its price list's price, else the normal price; without VAT. */
  price: number;
  base: number;
  special: boolean;
  on_hand: number | null;
}

/** "INV 12/2026", "CN 3/2026", or "Draft". */
export const docName = (d: { kind: Kind; number: number | null; year: number | null }) =>
  d.number && d.year ? `${d.kind === 'credit' ? t('inv.cn_prefix') : t('inv.inv_prefix')} ${d.number}/${d.year}` : t('inv.draft');
const left = (d: { total: number; paid: number }) => d.total - d.paid;
/** What is still open, in the document's own currency. */
const fxLeft = (d: { fx_total?: number | null; fx_paid?: number | null }) => (d.fx_total ?? 0) - (d.fx_paid ?? 0);

/* The same figures the server keeps (modules/invoicing lineFigures): rounded once per line, half away from zero. */
const round = (v: number) => Math.sign(v) * Math.round(Math.abs(v));
export function lineFigures(unitPrice: number, qty: number, discount: number, rate: number) {
  const gross = round((unitPrice * Math.round(qty * 1000)) / 1000);
  const net = gross - round((gross * Math.round(discount * 1000)) / 100000);
  const tax = round((net * Math.round(rate * 1000)) / 100000);
  return { net, tax, total: net + tax };
}
/** Net and VAT for each rate used, as printed under the lines. */
export function byRate(lines: { net: number; tax: number; tax_rate: number }[]) {
  const out = new Map<number, { net: number; tax: number }>();
  for (const l of lines) {
    const r = out.get(l.tax_rate) ?? { net: 0, tax: 0 };
    out.set(l.tax_rate, { net: r.net + l.net, tax: r.tax + l.tax });
  }
  return [...out.entries()].sort((a, b) => b[0] - a[0]);
}

export const qtyText = (n: number) => String(Math.round(n * 1000) / 1000);
export const parseNumber = (s: string) => {
  const v = Number(s.trim().replace(',', '.'));
  return s.trim() && Number.isFinite(v) ? v : null;
};
export const priceText = (minor: number) => (minor % 100 ? (minor / 100).toFixed(2) : String(minor / 100));

function InvoiceBadge({ d }: { d: InvoiceRow }) {
  if (d.state === 'draft') return <StatusBadge tone="neutral" icon="edit" label={t('inv.st_draft')} />;
  if (d.state === 'cancelled') return <StatusBadge tone="neutral" icon="x" label={t('inv.st_cancelled')} />;
  if (d.state === 'paid') return <StatusBadge tone="ok" icon="check" label={d.kind === 'credit' ? t('inv.st_used') : t('inv.st_paid')} />;
  if (d.kind === 'credit') return <StatusBadge tone="info" icon="undo" label={t('inv.st_credit_left')} />;
  if (d.days_overdue > 0) return <StatusBadge tone="danger" icon="alert" label={tn('inv.st_late', d.days_overdue)} />;
  if (d.paid > 0) return <StatusBadge tone="warn" icon="clock" label={t('inv.st_part')} />;
  return <StatusBadge tone="info" icon="clock" label={t('inv.st_open')} />;
}

type Tab = 'invoices' | 'quotes' | 'deliveries' | 'customers' | 'followup' | 'pricelists' | 'commissions' | 'settings';
const TAB_ICON: Record<Tab, IconName> = { invoices: 'invoice', quotes: 'note', deliveries: 'truck', customers: 'users', followup: 'bell', pricelists: 'percent', commissions: 'star', settings: 'settings' };

/** Selling to businesses: invoices and credit notes, the customers they go to, and what is owed. */
export function Invoices() {
  const { can } = useSession();
  const manage = can('invoicing.manage');
  const [params, setParams] = useSearchParams();
  const tabs: Tab[] = ['invoices', 'quotes', 'deliveries', 'customers', 'followup', 'pricelists', 'commissions', ...(can('invoicing.settings') ? (['settings'] as const) : [])];
  const tab = (tabs.includes(params.get('tab') as Tab) ? params.get('tab') : 'invoices') as Tab;
  const { data: overview, reload } = useAction<Overview>('invoicing.overview');
  const [version, setVersion] = useState(0);
  const [creating, setCreating] = useState<'invoice' | 'quote' | 'customer' | 'pricelist' | null>(null);
  const [newFor, setNewFor] = useState<string | undefined>();
  // A link from elsewhere (a construction certificate, the search) opens one invoice: /invoices?open=<id>.
  const [openId, setOpenId] = useState<string | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [deliveryId, setDeliveryId] = useState<string | null>(null);
  const openInvoice = (id: string) => (setQuoteId(null), setDeliveryId(null), setOpenId(id));
  const openQuote = (id: string) => (setOpenId(null), setDeliveryId(null), setQuoteId(id));
  const openDelivery = (id: string) => (setOpenId(null), setQuoteId(null), setDeliveryId(id));
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  const startInvoice = (customer?: string) => {
    setNewFor(customer);
    setCreating('invoice');
  };
  const startQuote = (customer?: string) => {
    setNewFor(customer);
    setCreating('quote');
  };
  useLinkParam('open', (id) => openInvoice(id));
  // A note on a quote or order links here: /invoices?tab=quotes&quote=<id>.
  useLinkParam('quote', (id) => openQuote(id));
  // Quick create: /invoices?new=invoice | quote | customer.
  useLinkParam('new', (kind) => {
    if (!manage) return;
    if (kind === 'invoice') startInvoice();
    else if (kind === 'quote') startQuote();
    else if (kind === 'customer') setCreating('customer');
  });

  return (
    <Shell>
      <div className="page invoices">
        <div className="page-head">
          <div>
            <h1>{t('inv.title')}</h1>
            <p className="muted">{t('inv.subtitle')}</p>
          </div>
          {manage ? (
            <div className="page-actions">
              {tab === 'customers' ? (
                <Button variant="primary" icon="plus" onClick={() => setCreating('customer')}>
                  {t('inv.new_customer')}
                </Button>
              ) : tab === 'quotes' ? (
                <Button variant="primary" icon="plus" onClick={() => startQuote()}>
                  {t('quote.new')}
                </Button>
              ) : tab === 'pricelists' ? (
                can('invoicing.pricelists') ? (
                  <Button variant="primary" icon="plus" onClick={() => setCreating('pricelist')}>
                    {t('pl.new')}
                  </Button>
                ) : null
              ) : tab === 'settings' || tab === 'deliveries' || tab === 'commissions' || tab === 'followup' ? null : (
                <Button variant="primary" icon="plus" onClick={() => startInvoice()}>
                  {t('inv.new_invoice')}
                </Button>
              )}
            </div>
          ) : null}
        </div>

        {!overview ? (
          <Skeleton h={88} r="var(--radius-lg)" />
        ) : (
          <dl className="fiscal-stats inv-stats">
            <div className="fiscal-stat">
              <dt>{t('inv.k_open')}</dt>
              <dd>{money(overview.open)}</dd>
            </div>
            <div className={`fiscal-stat${overview.overdue ? ' danger' : ''}`}>
              <dt>{t('inv.k_overdue')}</dt>
              <dd>
                {money(overview.overdue)}
                {overview.overdue_count ? <small> · {tn('inv.k_overdue_n', overview.overdue_count)}</small> : null}
              </dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('inv.k_month')}</dt>
              <dd>{money(overview.month_net)}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('inv.k_quotes')}</dt>
              <dd>
                {money(overview.quotes_total)}
                {overview.quotes ? <small> · {tn('inv.k_quotes_n', overview.quotes)}</small> : null}
              </dd>
            </div>
            <div className={`fiscal-stat${overview.orders ? ' warn' : ''}`}>
              <dt>{t('inv.k_orders')}</dt>
              <dd>{overview.orders}</dd>
            </div>
          </dl>
        )}

        <div className="segmented tabs" role="tablist" aria-label={t('inv.title')}>
          {tabs.map((k) => (
            <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setParams(k === 'invoices' ? {} : { tab: k }, { replace: true })}>
              <Icon name={TAB_ICON[k]} size={16} />
              {t(`inv.tab_${k}`)}
            </button>
          ))}
        </div>

        {tab === 'invoices' ? <InvoiceList key={version} onOpen={openInvoice} /> : null}
        {tab === 'quotes' ? <QuoteList key={version} onOpen={openQuote} /> : null}
        {tab === 'deliveries' ? <DeliveryList key={version} onOpen={openDelivery} /> : null}
        {tab === 'customers' ? <Customers key={version} onChange={changed} onOpenInvoice={openInvoice} onNewInvoice={startInvoice} onOpenQuote={openQuote} onNewQuote={startQuote} /> : null}
        {tab === 'pricelists' ? <PricelistsTab creating={creating === 'pricelist'} onCreated={() => (setCreating(null), void changed())} /> : null}
        {tab === 'followup' ? <FollowUpTab key={version} canRemind={can('invoicing.payments')} /> : null}
        {tab === 'commissions' ? <CommissionsTab key={version} onOpenInvoice={openInvoice} /> : null}
        {tab === 'settings' ? <SettingsTab /> : null}
      </div>
      {creating === 'invoice' ? (
        <InvoiceEditor
          detail={null}
          customer={newFor}
          onClose={() => (setCreating(null), void changed())}
          onSaved={async (id) => {
            setCreating(null);
            await changed();
            setOpenId(id);
          }}
        />
      ) : null}
      {creating === 'quote' ? (
        <InvoiceEditor
          detail={null}
          quote={null}
          customer={newFor}
          onClose={() => (setCreating(null), void changed())}
          onSaved={async (id) => {
            setCreating(null);
            await changed();
            openQuote(id);
          }}
        />
      ) : null}
      {creating === 'customer' ? <CustomerEditor onClose={() => setCreating(null)} onSaved={async () => (setCreating(null), await changed())} /> : null}
      {openId ? <InvoiceDialog key={openId} id={openId} onClose={() => setOpenId(null)} onOpen={openInvoice} onOpenQuote={openQuote} onChange={changed} /> : null}
      {quoteId ? <QuoteDialog key={quoteId} id={quoteId} onClose={() => setQuoteId(null)} onOpenInvoice={openInvoice} onOpenDelivery={openDelivery} onChange={changed} /> : null}
      {deliveryId ? <DeliveryDialog key={deliveryId} id={deliveryId} onClose={() => setDeliveryId(null)} onOpenQuote={openQuote} onChange={changed} /> : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Invoice list */

const FILTERS = ['open', 'overdue', 'draft', 'paid', 'all'] as const;

function InvoiceList({ onOpen }: { onOpen: (id: string) => void }) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('open');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const { data } = useAction<(InvoiceRow & { customer_name: string })[]>('invoicing.invoices', { filter, search: q || undefined });
  const extra = useCustomColumns('invoicing.invoice', data?.map((d) => d.id) ?? []);
  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="radiogroup" aria-label={t('inv.show')}>
          {FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {t(`inv.f_${f}`)}
            </button>
          ))}
        </div>
        <form
          className="search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(search.trim());
          }}
        >
          <Icon name="search" size={18} />
          <input className="input" placeholder={t('inv.search')} aria-label={t('inv.search')} value={search} onChange={(e) => setSearch(e.target.value)} onBlur={() => setQ(search.trim())} />
          {search ? <IconButton icon="x" label={t('common.clear')} onClick={() => (setSearch(''), setQ(''))} /> : null}
        </form>
      </div>
      <section className="list-card" aria-label={t('inv.tab_invoices')}>
        {!data ? (
          <Skeleton h={160} />
        ) : data.length === 0 ? (
          <EmptyState icon="invoice" title={q ? t('order.no_results') : t('inv.none')} body={q ? undefined : t('inv.none_body')} />
        ) : (
          data.map((d) => (
            <button key={d.id} className="list-row stock-row" onClick={() => onOpen(d.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name={d.kind === 'credit' ? 'undo' : 'invoice'} size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">
                  {docName(d)} · {d.customer_name}
                </span>
                <span className="list-sub">
                  {[day(d.date), d.kind === 'invoice' && d.state === 'issued' ? t('inv.due_on', { day: day(d.due_date) }) : null, d.reference, extra.line(d.id)]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className="stock-qty">
                {d.currency ? (
                  <>
                    <strong>{moneyIn(d.fx_total ?? 0, d.currency)}</strong>
                    <span className="muted">{d.state === 'issued' && d.paid !== 0 ? t('inv.left', { amount: moneyIn(fxLeft(d), d.currency) }) : money(d.total)}</span>
                  </>
                ) : (
                  <>
                    <strong>{money(d.total)}</strong>
                    {d.state === 'issued' && d.paid !== 0 ? <span className="muted">{t('inv.left', { amount: money(left(d)) })}</span> : null}
                  </>
                )}
              </span>
              <InvoiceBadge d={d} />
            </button>
          ))
        )}
      </section>
    </>
  );
}

/* ---------------------------------------------------------------- One invoice */

function InvoiceDialog({ id, onClose, onOpen, onOpenQuote, onChange }: { id: string; onClose: () => void; onOpen: (id: string) => void; onOpenQuote: (id: string) => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { data: d, reload } = useAction<Detail>('invoicing.invoice', { invoice: id });
  const refresh = async () => {
    await Promise.all([reload(), onChange()]);
  };
  if (!d) {
    return (
      <Dialog title={t('inv.invoice')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  }
  if (d.invoice.state === 'draft' && can('invoicing.manage')) {
    return <InvoiceEditor detail={d} onClose={onClose} onSaved={async () => await refresh()} onIssued={refresh} />;
  }
  return <InvoiceView d={d} onClose={onClose} onOpen={onOpen} onOpenQuote={onOpenQuote} onChange={refresh} />;
}

export interface EditLine {
  product: string;
  name: string;
  unit: Unit;
  qty: string;
  price: string;
  discount: string;
  vat: number;
  /** On an invoice made from a sales order: the order line it bills. */
  order_line?: string | null;
}
export const blankLine = (): EditLine => ({ product: '', name: '', unit: 'pcs', qty: '1', price: '', discount: '', vat: standardRateNow() });

/**
 * A new invoice or a draft (who it is for, the lines, then save or issue), or a quote: the same
 * lines with the date it is valid until instead of a due date.
 */
export function InvoiceEditor({
  detail,
  quote,
  customer: forCustomer,
  onClose,
  onSaved,
  onIssued,
}: {
  detail: Detail | null;
  /** Writing a quote: the one being changed, or null for a new one. */
  quote?: QuoteDetail | null;
  customer?: string;
  onClose: () => void;
  onSaved: (id: string) => Promise<void>;
  onIssued?: () => Promise<void>;
}) {
  const isQuote = quote !== undefined;
  const { can } = useSession();
  const { fail, toast } = useUi();
  const { busy, run } = useSave();
  const places = usePlaces();
  const { data: customers, reload: reloadCustomers } = useAction<Customer[]>('invoicing.customers', { filter: 'all' });
  const inv = detail?.invoice;
  const q = quote?.quote;
  const doc = inv ?? q;
  const [customer, setCustomer] = useState(doc?.customer ?? forCustomer ?? '');
  // Prices follow the customer's price list.
  const { data: products } = useAction<Sellable[]>('invoicing.products', { customer: customer || null });
  const [date, setDate] = useState(doc?.date ?? todayIso());
  const [due, setDue] = useState(inv ? inv.due_date : q ? q.valid_until : '');
  // The payment term: the draft's, else (untouched) the customer's or the business's default.
  const { data: terms } = usePaymentTerms();
  const { data: invSettings } = useAction<Settings>('invoicing.settings');
  const [term, setTerm] = useState<string | null>(inv ? (inv.payment_term ?? '') : null);
  const [delivery, setDelivery] = useState(q?.delivery_date ?? '');
  const [reference, setReference] = useState(doc?.reference ?? '');
  const [note, setNote] = useState(doc?.note ?? '');
  const [place, setPlace] = useState(doc?.location ?? places.here?.id ?? '');
  // Who sold it; a new document is the writer's own when they are on commission.
  const { me } = useSession();
  const { data: sellers } = useAction<{ id: string; name: string; on_commission: boolean }[]>('invoicing.salespeople');
  const [seller, setSeller] = useState<string | null>(doc ? (doc.salesperson ?? '') : null);
  const sellerNow = seller ?? (sellers?.find((s) => s.id === me?.userId && s.on_commission) ? me!.userId : '');
  // Invoices (not quotes, not those for a sales order) can be in another currency.
  const { data: fxList } = useCurrencies();
  const [cur, setCur] = useState(inv?.currency ?? '');
  const [rateTyped, setRateTyped] = useState(inv?.rate ? String(inv.rate / 1_000_000) : '');
  const dayRate = useDayRate(cur, date);
  const rate = cur ? effectiveRate(rateTyped, dayRate) : null;
  const fxOffered = !isQuote && !detail?.order && !!fxList && (fxList.currencies.some((c) => c.active) || !!cur);
  const fmt = (n: number) => moneyIn(n, cur || null);
  const toEdit = (l: Pick<Line, 'product' | 'name' | 'unit' | 'qty' | 'unit_price' | 'discount' | 'tax_rate'> & { fx_unit_price?: number | null; order_line?: string | null }): EditLine => ({
    product: l.product ?? '',
    name: l.name,
    unit: l.unit,
    qty: qtyText(l.qty),
    price: priceText(inv?.currency ? (l.fx_unit_price ?? l.unit_price) : l.unit_price),
    discount: l.discount ? String(l.discount) : '',
    vat: l.tax_rate,
    order_line: l.order_line ?? null,
  });
  const [lines, setLines] = useState<EditLine[]>((detail?.lines ?? quote?.lines)?.map((l) => toEdit(l as Line)) ?? [blankLine()]);
  // A quote's optional extras: offered to the customer, not counted until added.
  const [opts, setOpts] = useState<EditLine[]>(quote?.options?.map((l) => toEdit(l as Line)) ?? []);
  const [overLimit, setOverLimit] = useState<{ credit_limit: number; open: number; total: number; can_override: boolean } | null>(null);
  const [needsDetails, setNeedsDetails] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | 'new' | null>(null);
  const [cancelling, setCancelling] = useState(false);
  // Quotes have no fields of their own; invoices may.
  const cf = useCustomFields('invoicing.invoice', isQuote ? null : inv?.id);
  const [tried, setTried] = useState(false);
  const fieldsOk = () => {
    setTried(true);
    return isQuote || cf.valid;
  };

  const byId = new Map((products ?? []).map((p) => [p.id, p]));
  const parseLine = (l: EditLine) => {
    const qty = parseNumber(l.qty);
    const price = parseMoney(l.price);
    const discount = l.discount.trim() ? parseNumber(l.discount) : 0;
    const ok = !!l.name.trim() && qty !== null && qty > 0 && price !== null && discount !== null && discount >= 0 && discount <= 100;
    return { ok, qty, price, discount, figures: ok ? lineFigures(price!, qty!, discount!, l.vat) : null };
  };
  const parsed = lines.map(parseLine);
  const parsedOpts = opts.map(parseLine);
  const filled = lines.map((l, i) => ({ l, p: parsed[i]! })).filter(({ l }) => l.name.trim() || l.price.trim() || l.product);
  const filledOpts = opts.map((l, i) => ({ l, p: parsedOpts[i]! })).filter(({ l }) => l.name.trim() || l.price.trim() || l.product);
  const bad = filled.some(({ p }) => !p.ok) || filledOpts.some(({ p }) => !p.ok);
  const totals = filled.reduce((s, { l, p }) => (p.figures ? { net: s.net + p.figures.net, tax: s.tax + p.figures.tax, rates: [...s.rates, { net: p.figures.net, tax: p.figures.tax, tax_rate: l.vat }] } : s), {
    net: 0,
    tax: 0,
    rates: [] as { net: number; tax: number; tax_rate: number }[],
  });
  const cust = customers?.find((c) => c.id === customer);
  const termNow = term ?? (terms ? (cust?.payment_term && terms.some((x) => x.id === cust.payment_term) ? cust.payment_term : (invSettings?.payment_term ?? '')) : '');
  const termRow = terms?.find((x) => x.id === termNow) ?? null;
  const dueBad = !termRow && !!due && due < date;
  const deliveryBad = !!delivery && delivery < date;
  const canSave = !!customer && filled.length > 0 && !bad && !!date && !dueBad && !deliveryBad && (!cur || rate !== null);

  const patched = (list: EditLine[], i: number, patch: Partial<EditLine>) =>
    list.map((l, j) => {
      if (j !== i) return l;
      const next = { ...l, ...patch };
      const p = patch.product ? byId.get(patch.product) : undefined;
      // A product's price is in the business's currency: in another one it is converted at the rate.
      const price = p ? (cur && rate ? Math.round((p.price * 1_000_000) / rate) : p.price) : 0;
      if (p) Object.assign(next, { name: p.name, price: priceText(price), vat: knownRateOr(p.tax_rate) });
      return next;
    });
  const set = (i: number, patch: Partial<EditLine>) => setLines(patched(lines, i, patch));
  const setOpt = (i: number, patch: Partial<EditLine>) => setOpts(patched(opts, i, patch));
  const rowOut = ({ l, p }: { l: EditLine; p: ReturnType<typeof parseLine> }) => ({
    product: l.product || null,
    name: l.name.trim(),
    unit: l.unit,
    qty: p.qty,
    unit_price: p.price,
    discount: p.discount,
    tax_rate: l.vat,
    order_line: l.order_line ?? null,
  });

  // A new invoice that failed to issue is kept as a draft: later tries update that one.
  const [savedId, setSavedId] = useState<string | undefined>();
  const body = () => ({
    invoice: inv?.id ?? savedId,
    customer,
    date,
    due_date: termRow ? null : due || null,
    payment_term: termNow || null,
    location: places.multi ? place || null : null,
    reference: reference.trim() || null,
    note: note.trim() || null,
    lines: filled.map(rowOut),
    currency: cur || null,
    rate: cur && rateTyped.trim() ? Number(rateTyped.trim().replace(',', '.')) : null,
    ...(sellers ? { salesperson: sellerNow || null } : {}),
  });
  const quoteBody = () => {
    const { invoice: _i, due_date: _d, currency: _c, rate: _r, payment_term: _t, ...rest } = body();
    return { ...rest, quote: q?.id ?? savedId, valid_until: due || null, delivery_date: delivery || null, lines: rest.lines.map(({ order_line: _o, ...l }) => l), options: filledOpts.map(rowOut).map(({ order_line: _o, ...l }) => l) };
  };
  const save = () =>
    fieldsOk() &&
    void run(
      async () => {
        const saved = await action<{ id: string }>(isQuote ? 'invoicing.save_quote' : 'invoicing.save_draft', isQuote ? quoteBody() : body());
        if (!isQuote) await cf.save(saved.id);
        await onSaved(saved.id);
      },
      isQuote ? t('quote.saved') : t('inv.draft_saved'),
    );
  const issue = async (override = false) => {
    if (!fieldsOk()) return;
    try {
      const saved = await action<{ id: string }>('invoicing.save_draft', body());
      const id = saved.id;
      setSavedId(id);
      await cf.save(id);
      const done = await action<InvoiceRow>('invoicing.issue', { invoice: id, override_credit: override || undefined });
      toast(t('inv.issued', { name: docName(done) }), 'ok');
      setOverLimit(null);
      setNeedsDetails(false);
      if (detail) await onIssued?.();
      else await onSaved(id);
    } catch (err) {
      if (err instanceof ApiError && err.details && 'credit_limit' in err.details) {
        setOverLimit(err.details as never);
      } else if (err instanceof ApiError && err.details && 'missing' in err.details) {
        setNeedsDetails(true);
      } else fail(err);
    }
  };
  const [issuing, setIssuing] = useState(false);
  const doIssue = async (override = false) => {
    setIssuing(true);
    try {
      await issue(override);
    } finally {
      setIssuing(false);
    }
  };
  const cancel = () =>
    void run(async () => {
      await action('invoicing.cancel_draft', { invoice: inv!.id, reason: null });
      await onIssued?.();
      onClose();
    }, t('inv.draft_deleted'));

  /** One line of the editor: the product or typed text, quantity, price, discount, VAT and amount. */
  const lineRow = (l: EditLine, i: number, p: ReturnType<typeof parseLine>, onSet: (i: number, patch: Partial<EditLine>) => void, onRemove: () => void, removeDisabled: boolean, removeLabel: string) => {
    if (!products) return null;
    const product = l.product ? byId.get(l.product) : undefined;
    const touched = !!(l.name.trim() || l.price.trim() || l.product);
    return (
                <div key={i} className="inv-line" role="row">
                  <span className="inv-what">
                    <select className="input" aria-label={t('inv.product')} value={l.product} onChange={(e) => onSet(i, { product: e.target.value })}>
                      <option value="">{t('inv.typed_line')}</option>
                      {products.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                          {x.sku ? ` · ${x.sku}` : ''}
                        </option>
                      ))}
                    </select>
                    <input className={`input${touched && !l.name.trim() ? ' invalid' : ''}`} aria-label={t('inv.description')} placeholder={t('inv.description')} value={l.name} maxLength={200} onChange={(e) => onSet(i, { name: e.target.value })} />
                    {product && (product.on_hand !== null || product.special) ? (
                      <small className={product.on_hand !== null && product.on_hand < (p.qty ?? 0) ? 'text-danger' : 'muted'}>
                        {[
                          product.on_hand !== null ? t('inv.in_stock', { qty: qtyText(product.on_hand) }) : null,
                          product.special && cust?.pricelist_name ? t('inv.list_price', { list: cust.pricelist_name, price: money(product.base) }) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </small>
                    ) : null}
                  </span>
                  <span className="inv-qty">
                    <input className={`input${touched && (p.qty === null || p.qty <= 0) ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('inv.qty')} value={l.qty} onChange={(e) => onSet(i, { qty: e.target.value })} />
                    <select className="input" aria-label={t('inv.unit')} value={l.unit} onChange={(e) => onSet(i, { unit: e.target.value as Unit })}>
                      {UNITS.map((u) => (
                        <option key={u} value={u}>
                          {t(`inv.u_${u}` as MessageKey)}
                        </option>
                      ))}
                    </select>
                  </span>
                  <input className={`input${touched && p.price === null ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('inv.unit_price')} placeholder="0" value={l.price} onChange={(e) => onSet(i, { price: e.target.value })} />
                  <span className="input-unit">
                    <input className={`input${p.discount === null || (p.discount ?? 0) > 100 ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('inv.discount')} placeholder="0" value={l.discount} onChange={(e) => onSet(i, { discount: e.target.value })} />
                    <span>%</span>
                  </span>
                  <TaxRateSelect label={t('inv.vat')} value={l.vat} onChange={(r) => onSet(i, { vat: r })} />
                  <span className="num">{p.figures ? fmt(p.figures.net) : '—'}</span>
                  <IconButton icon="trash" label={removeLabel} disabled={removeDisabled} onClick={onRemove} />
                </div>
    );
  };

  const title = isQuote ? (q ? `${t(q.order ? 'quote.so_prefix' : 'quote.q_prefix')} ${q.name} · ${quote!.customer.name}` : t('quote.new')) : detail ? `${t('inv.draft')} · ${detail.customer.name}` : t('inv.new_invoice');
  const working = busy || issuing;

  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <span className="inv-foot-total">
            <span className="muted">{t('inv.total')}</span>
            <strong>{fmt(totals.net + totals.tax)}</strong>
          </span>
          {isQuote ? (
            <span className="foot-actions">
              <Button variant="ghost" onClick={onClose} disabled={busy}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!canSave}>
                {t('quote.save')}
              </Button>
            </span>
          ) : (
          <span className="foot-actions">
            {detail ? (
              <Button variant="ghost" icon="trash" onClick={() => setCancelling(true)} disabled={working}>
                {t('inv.delete_draft')}
              </Button>
            ) : null}
            <Button icon="edit" onClick={save} busy={busy} disabled={!canSave || issuing}>
              {t('inv.save_draft')}
            </Button>
            <Button variant="primary" icon="check" onClick={() => void doIssue()} busy={issuing} disabled={!canSave || busy}>
              {t('inv.issue')}
            </Button>
          </span>
          )}
        </>
      }
    >
      {!customers || !products ? (
        <Skeleton h={280} />
      ) : (
        <div className="form-stack">
          {detail?.order ? (
            <p className="callout callout-info">
              <Icon name="bag" size={16} />
              <span>{t('inv.draft_from_order', { name: quoteName({ ...detail.order, order: true }) })}</span>
            </p>
          ) : null}
          {cancelling ? (
            <div className="callout callout-warn confirm-row">
              <Icon name="alert" size={16} />
              <span>{t('inv.delete_confirm')}</span>
              <Button variant="ghost" size="sm" onClick={() => setCancelling(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" size="sm" onClick={cancel} busy={busy}>
                {t('inv.delete_draft')}
              </Button>
            </div>
          ) : null}
          {overLimit ? (
            <div className="callout callout-danger" role="alert">
              <Icon name="alert" size={20} />
              <div>
                <p className="strong">{t('inv.over_limit', { name: cust?.name ?? '' })}</p>
                <p className="muted">{t('inv.over_limit_body', { limit: money(overLimit.credit_limit), open: money(overLimit.open), total: money(overLimit.total) })}</p>
              </div>
              {overLimit.can_override && can('invoicing.override_credit') ? (
                <Button variant="danger" icon="check" onClick={() => void doIssue(true)} busy={issuing}>
                  {t('inv.issue_anyway')}
                </Button>
              ) : null}
            </div>
          ) : null}
          {needsDetails && cust ? (
            <div className="callout callout-warn" role="alert">
              <Icon name="alert" size={20} />
              <div>
                <p className="strong">{t('inv.needs_details', { name: cust.name })}</p>
                <p className="muted">{t('inv.needs_details_body')}</p>
              </div>
              <Button icon="edit" onClick={() => setEditingCustomer(cust)}>
                {t('inv.fix_customer')}
              </Button>
            </div>
          ) : null}
          <div className="form-grid">
            <Field label={t('inv.customer')} required hint={cust ? customerHint(cust) : undefined}>
              <select className="input" value={customer} onChange={(e) => (setCustomer(e.target.value), setOverLimit(null), setNeedsDetails(false))} data-autofocus={!detail || undefined}>
                <option value="">{t('inv.pick_customer')}</option>
                {customers
                  .filter((c) => c.active || c.id === customer)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.tax_id ? ` · ${c.tax_id}` : ''}
                    </option>
                  ))}
              </select>
            </Field>
            <div className="field inv-new-customer">
              {can('invoicing.manage') ? (
                <Button icon="plus" onClick={() => setEditingCustomer('new')}>
                  {t('inv.new_customer')}
                </Button>
              ) : null}
            </div>
            <Field label={isQuote ? t('quote.date') : t('inv.date')} required>
              <input className="input" type="date" value={date} max={isQuote ? undefined : todayIso()} onChange={(e) => setDate(e.target.value)} />
            </Field>
            {isQuote ? (
              <>
                <Field label={t('quote.valid_until')} hint={due ? undefined : t('quote.valid_auto')} error={dueBad ? t('quote.valid_before') : null}>
                  <input className="input" type="date" value={due} min={date} onChange={(e) => setDue(e.target.value)} />
                </Field>
                <Field label={t('quote.delivery_date')} hint={t('quote.delivery_hint')} error={deliveryBad ? t('quote.delivery_before') : null}>
                  <input className="input" type="date" value={delivery} min={date} onChange={(e) => setDelivery(e.target.value)} />
                </Field>
              </>
            ) : (
              <>
                {terms?.length || termNow ? (
                  <Field label={t('terms.field')} hint={termRow ? termSummary(termRow) : t('terms.field_hint')}>
                    <TermSelect terms={terms} value={termNow} onChange={setTerm} noneLabel={t('terms.none_due_date')} />
                  </Field>
                ) : null}
                {termRow ? null : (
                  <Field label={t('inv.due_date')} hint={due ? undefined : t('inv.due_auto', { days: cust?.payment_days ?? 0 })} error={dueBad ? t('inv.due_before') : null}>
                    <input className="input" type="date" value={due} min={date} onChange={(e) => setDue(e.target.value)} />
                  </Field>
                )}
              </>
            )}
            <Field label={t('inv.reference')} hint={t('inv.reference_hint')}>
              <input className="input" value={reference} maxLength={80} onChange={(e) => setReference(e.target.value)} />
            </Field>
            {sellers && sellers.length ? (
              <Field label={t('com.salesperson')} hint={t('com.salesperson_hint')}>
                <select className="input" value={sellerNow} onChange={(e) => setSeller(e.target.value)}>
                  <option value="">{t('com.no_salesperson')}</option>
                  {sellers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {s.on_commission ? ` · ${t('com.on_commission')}` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {fxOffered ? <CurrencyFields list={fxList!} currency={cur} onCurrency={setCur} rate={rateTyped} onRate={setRateTyped} date={date} dayRate={dayRate} /> : null}
            {places.multi ? (
              <Field label={t('inv.branch')}>
                <select className="input" value={place} onChange={(e) => setPlace(e.target.value)}>
                  {places.places.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
          </div>

          <div className="inv-lines" role="table" aria-label={t('inv.lines')}>
            <div className="inv-line head" role="row">
              <span role="columnheader">{t('inv.what')}</span>
              <span role="columnheader">{t('inv.qty')}</span>
              <span role="columnheader">{t('inv.unit_price')}</span>
              <span role="columnheader">{t('inv.discount')}</span>
              <span role="columnheader">{t('inv.vat')}</span>
              <span role="columnheader" className="num">
                {t('inv.net')}
              </span>
              <span />
            </div>
            {lines.map((l, i) => lineRow(l, i, parsed[i]!, set, () => setLines(lines.filter((_, j) => j !== i)), lines.length === 1, t('inv.remove_line')))}
          </div>
          <div className="row-actions">
            <Button icon="plus" onClick={() => setLines([...lines, blankLine()])}>
              {t('inv.add_line')}
            </Button>
          </div>
          {isQuote ? (
            <section className="quote-opts" aria-label={t('qopt.title')}>
              <div className="quote-opts-head">
                <h3>
                  <Icon name="plus" size={16} /> {t('qopt.title')}
                </h3>
                <p className="muted">{t('qopt.hint')}</p>
              </div>
              {opts.length ? (
                <div className="inv-lines" role="table" aria-label={t('qopt.title')}>
                  {opts.map((l, i) => lineRow(l, i, parsedOpts[i]!, setOpt, () => setOpts(opts.filter((_, j) => j !== i)), false, t('qopt.remove')))}
                </div>
              ) : null}
              <div className="row-actions">
                <Button icon="plus" variant="ghost" onClick={() => setOpts([...opts, blankLine()])}>
                  {t('qopt.add')}
                </Button>
              </div>
            </section>
          ) : null}
          <Totals
            net={totals.net}
            tax={totals.tax}
            rates={byRate(totals.rates)}
            currency={cur || null}
            base={cur && rate ? { total: totals.rates.reduce((s, r) => s + toBase(r.net, rate) + toBase(r.tax, rate), 0), rate, code: fxList?.base ?? '' } : null}
          />
          {!isQuote && termRow && totals.net + totals.tax > 0 ? (
            <div className="field">
              <span className="field-label">{t('terms.when_paid')}</span>
              <SchedulePreview term={cur ? { ...termRow, discount_percent: 0 } : termRow} date={date} total={totals.net + totals.tax} fmt={fmt} />
            </div>
          ) : null}
          <Field label={t('inv.note')} hint={isQuote ? t('quote.note_hint') : t('inv.note_hint')}>
            <textarea className="input textarea" rows={2} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {isQuote ? null : <CustomFieldInputs cf={cf} tried={tried} />}
        </div>
      )}
      {editingCustomer ? (
        <CustomerEditor
          customer={editingCustomer === 'new' ? undefined : editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSaved={async (c) => {
            setEditingCustomer(null);
            setNeedsDetails(false);
            await reloadCustomers();
            setCustomer(c.id);
          }}
        />
      ) : null}
    </Dialog>
  );
}

export function customerHint(c: Customer) {
  return [c.tax_id, c.town, c.payment_days ? tn('inv.pays_in', c.payment_days) : t('inv.pays_now'), c.open ? t('inv.owes', { amount: money(c.open) }) : null].filter(Boolean).join(' · ');
}

export function Totals({
  net,
  tax,
  rates,
  currency,
  base,
}: {
  net: number;
  tax: number;
  rates: [number, { net: number; tax: number }][];
  /** The document's currency, when it is not the business's own. */
  currency?: string | null;
  /** In another currency: the total in the business's currency, at the document's rate. */
  base?: { total: number; rate: number; code: string } | null;
}) {
  const m = (n: number) => moneyIn(n, currency);
  return (
    <dl className="inv-totals">
      {rates.length > 1
        ? rates.map(([r, v]) => (
            <div key={r} className="muted">
              <dt>{t('inv.vat_on', { rate: r, net: m(v.net) })}</dt>
              <dd>{m(v.tax)}</dd>
            </div>
          ))
        : null}
      <div>
        <dt>{t('inv.total_net')}</dt>
        <dd>{m(net)}</dd>
      </div>
      <div>
        <dt>{rates.length === 1 ? t('inv.vat_rate', { rate: rates[0]![0] }) : t('inv.vat')}</dt>
        <dd>{m(tax)}</dd>
      </div>
      <div className="inv-grand">
        <dt>{t('inv.total')}</dt>
        <dd>{m(net + tax)}</dd>
      </div>
      {currency && base ? (
        <div className="muted">
          <dt>{t('fx.total_in', { base: base.code, code: currency, rate: rateText(base.rate) })}</dt>
          <dd>{money(base.total)}</dd>
        </div>
      ) : null}
    </dl>
  );
}

/** Whether a card payment link can be sent: the Payments module is in the plan and set up. */
function useCardReady(want: boolean) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!want) return;
    let live = true;
    action<{ ready: { ready: boolean } | null }>('payments.ready_check').then(
      (r) => live && setReady(!!r.ready?.ready),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [want]);
  return want && ready;
}

/** An issued document: the paper as it prints, and what can still happen to it. */
function InvoiceView({ d, onClose, onOpen, onOpenQuote, onChange }: { d: Detail; onClose: () => void; onOpen: (id: string) => void; onOpenQuote: (id: string) => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const inv = d.invoice;
  const [mode, setMode] = useState<'pay' | 'credit' | 'apply' | null>(null);
  const [printing, setPrinting] = useState(false);
  const [cardLink, setCardLink] = useState(false);
  const [einvoice, setEinvoice] = useState(false);
  const look = useDocLook('invoice');
  const open = inv.state === 'issued';
  const card = useCardReady(open && inv.kind === 'invoice' && can('payments.links'));
  const print = () => {
    setPrinting(true);
    // Let the sheet render, then print it; it goes away once the print dialog closes.
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 50);
  };
  const creditable = inv.kind === 'invoice' && (inv.state === 'issued' || inv.state === 'paid') && d.lines.some((l) => l.qty - l.credited > 0);

  return (
    <Dialog
      title={`${docName(inv)} · ${d.customer.name}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions">
            <Button icon="printer" onClick={print}>
              {t('inv.print')}
            </Button>
            <LookPicker {...look} />
            {inv.state !== 'draft' && inv.state !== 'cancelled' ? (
              <Button icon="download" onClick={() => setEinvoice(true)}>
                {t('einv.button')}
              </Button>
            ) : null}
            {creditable && can('invoicing.manage') ? (
              <Button icon="undo" onClick={() => setMode('credit')}>
                {t('inv.credit_note')}
              </Button>
            ) : null}
            {card ? (
              <Button icon="card" onClick={() => setCardLink(true)}>
                {t('inv.card_link')}
              </Button>
            ) : null}
          </span>
          {open && can('invoicing.payments') ? (
            inv.kind === 'invoice' ? (
              <Button variant="primary" icon="cash" onClick={() => setMode('pay')}>
                {t('inv.record_payment')}
              </Button>
            ) : (
              <Button variant="primary" icon="check" onClick={() => setMode('apply')}>
                {t('inv.use_credit')}
              </Button>
            )
          ) : null}
        </>
      }
    >
      <div className="form-stack">
        <div className="item-summary">
          <div>
            <span className="muted">{t('inv.total')}</span>
            <strong>{inv.currency ? moneyIn(inv.fx_total ?? 0, inv.currency) : money(inv.total)}</strong>
            {inv.currency ? <span className="fx-base">{t('fx.at_rate', { amount: money(inv.total), rate: rateText(inv.rate ?? 0) })}</span> : null}
          </div>
          {inv.kind === 'invoice' ? (
            <div>
              <span className="muted">{t('inv.due_date')}</span>
              <strong className={inv.days_overdue > 0 && open ? 'text-danger' : ''}>{day(inv.due_date)}</strong>
            </div>
          ) : null}
          {open ? (
            <div>
              <span className="muted">{inv.kind === 'credit' ? t('inv.credit_left') : t('inv.left_to_pay')}</span>
              <strong>{inv.currency ? moneyIn(Math.abs(fxLeft(inv)), inv.currency) : money(Math.abs(left(inv)))}</strong>
            </div>
          ) : null}
          <InvoiceBadge d={inv} />
        </div>
        {inv.schedule?.length ? <InstalmentList inv={inv} /> : null}
        {inv.discount_amount && inv.discount_until && inv.state === 'issued' && inv.paid === 0 && todayIso() <= inv.discount_until ? (
          <p className="callout callout-info">
            <Icon name="percent" size={16} />
            <span>{t('terms.discount_open', { pct: inv.discount_percent ?? 0, amount: money(inv.total - inv.discount_amount), date: day(inv.discount_until) })}</span>
          </p>
        ) : null}
        {inv.kind === 'credit' && inv.reason ? (
          <p className="callout callout-info">
            <Icon name="undo" size={16} />
            <span>{t('inv.credit_reason', { reason: inv.reason })}</span>
          </p>
        ) : null}
        {d.fiscal ? <FiscalBox d={d} f={d.fiscal} /> : null}
        {d.order ? (
          <button className="callout callout-info link-callout" onClick={() => onOpenQuote(d.order!.id)}>
            <Icon name="bag" size={16} />
            <span>{t('inv.from_order', { name: quoteName({ ...d.order, order: true }) })}</span>
            <Icon name="chevronRight" size={16} />
          </button>
        ) : null}
        {einvoice ? <EInvoiceDialog invoice={inv.id} onClose={() => setEinvoice(false)} /> : null}
        {mode === 'pay' ? <PayBox d={d} onDone={async () => (setMode(null), await onChange())} onCancel={() => setMode(null)} /> : null}
        {cardLink ? (
          <NewLink
            currency={inv.currency ?? d.base_currency ?? 'ALL'}
            purpose={{ module: 'invoicing', record: inv.id, title: docName(inv), email: d.customer.email, name: d.customer.name }}
            onClose={() => setCardLink(false)}
          />
        ) : null}
        {mode === 'credit' ? <CreditBox d={d} onCancel={() => setMode(null)} onDone={async (id) => (setMode(null), await onChange(), onOpen(id))} /> : null}
        {mode === 'apply' ? <ApplyCreditBox d={d} onCancel={() => setMode(null)} onDone={async () => (setMode(null), await onChange())} /> : null}

        <CustomFieldsView target="invoicing.invoice" record={inv.id} version={d} />
        <div className="inv-preview">
          <InvoicePaper d={d} look={look.paper} />
        </div>

        {d.payments.length ? (
          <section className="item-moves">
            <h3>{t('inv.payments')}</h3>
            <div className="list-card">
              {d.payments.map((p) => (
                <div key={p.id} className="list-row compact-move">
                  <span className="list-icon" aria-hidden="true">
                    <Icon name={p.method === 'credit_note' ? 'undo' : METHODS.find((m) => m.key === p.method)!.icon} size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">
                      {p.method === 'credit_note' && p.other_kind ? docName({ kind: p.other_kind, number: p.other_number, year: p.other_year }) : t(`inv.pm_${p.method}` as MessageKey)}
                    </span>
                    <span className="list-sub">
                      {[
                        day(p.paid_on),
                        p.by_name,
                        p.reference,
                        p.fx_amount && p.rate ? t('fx.paid_at', { rate: rateText(p.rate), amount: money(p.amount + (p.fx_diff ?? 0)) }) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      {p.fx_diff ? <span className={p.fx_diff > 0 ? 'fx-gain' : 'fx-loss'}> · {t(p.fx_diff > 0 ? 'fx.gain' : 'fx.loss', { amount: money(Math.abs(p.fx_diff)) })}</span> : null}
                    </span>
                  </span>
                  <strong>{p.fx_amount ? moneyIn(p.fx_amount, inv.currency) : inv.currency && inv.total ? moneyIn(Math.round((Math.abs(p.amount) * (inv.fx_total ?? 0)) / inv.total), inv.currency) : money(Math.abs(p.amount))}</strong>
                </div>
              ))}
            </div>
          </section>
        ) : null}
        {d.related.filter((r) => r.id !== inv.id).length ? (
          <section className="item-moves">
            <h3>{t('inv.related')}</h3>
            <div className="list-card">
              {d.related
                .filter((r) => r.id !== inv.id)
                .map((r) => (
                  <button key={r.id} className="list-row compact-move" onClick={() => onOpen(r.id)}>
                    <span className="list-icon" aria-hidden="true">
                      <Icon name={r.kind === 'credit' ? 'undo' : 'invoice'} size={18} />
                    </span>
                    <span className="list-main">
                      <span className="list-title">{docName(r)}</span>
                      <span className="list-sub">{day(r.date)}</span>
                    </span>
                    <strong>{money(r.total)}</strong>
                  </button>
                ))}
            </div>
          </section>
        ) : null}
        <RecordNotes model="invoicing.invoice" record={inv.id} version={d} />
      </div>
      {printing
        ? createPortal(
            <div className="print-sheet print-invoice">
              <InvoicePaper d={d} look={look.paper} />
            </div>,
            document.body,
          )
        : null}
    </Dialog>
  );
}

const EINVOICE_FORMATS = ['peppol', 'xrechnung', 'cii', 'fatturapa'] as const;
type EFormat = (typeof EINVOICE_FORMATS)[number];
interface EInvoice {
  format: EFormat;
  formats: EFormat[];
  default: EFormat;
  problems: string[];
  filename: string | null;
  xml: string | null;
}

/** A structured e-invoice (Peppol, XRechnung, Factur-X, FatturaPA) for the customer's own software. */
function EInvoiceDialog({ invoice, onClose }: { invoice: string; onClose: () => void }) {
  const [format, setFormat] = useState<EFormat | null>(null);
  const { data } = useAction<EInvoice>('invoicing.einvoice', format ? { invoice, format } : { invoice });
  const shown = format ?? data?.format ?? null;
  const ready = data && data.format === shown && data.xml && data.filename;
  const download = () => saveBlob(new Blob([data!.xml!], { type: 'application/xml' }), data!.filename!);
  return (
    <Dialog
      title={t('einv.title')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button variant="primary" icon="download" onClick={download} disabled={!ready}>
            {t('einv.download')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('einv.intro')}</p>
        {!data ? (
          <Skeleton h={120} />
        ) : (
          <>
            <fieldset className="einv-formats">
              <legend className="field-label">{t('einv.format')}</legend>
              {data.formats.map((f) => (
                <label key={f} className={`einv-format${shown === f ? ' is-on' : ''}`}>
                  <input type="radio" name="einv-format" value={f} checked={shown === f} onChange={() => setFormat(f)} />
                  <span>
                    <strong>
                      {t(`einv.f_${f}`)}
                      {f === data.default ? <span className="badge badge-info einv-usual">{t('einv.usual')}</span> : null}
                    </strong>
                    <span className="muted">{t(`einv.f_${f}_hint`)}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            {data.format !== shown ? (
              <Skeleton h={48} />
            ) : data.problems.length ? (
              <div className="callout callout-warn einv-problems" role="status">
                <Icon name="alert" size={16} />
                <div>
                  <strong>{tn('einv.missing', data.problems.length)}</strong>
                  <ul>
                    {data.problems.map((p) => (
                      <li key={p}>{t(`einv.p_${p}` as MessageKey)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <p className="callout callout-ok" role="status">
                <Icon name="checkCircle" size={16} />
                <span>{t('einv.ready', { file: data.filename ?? '' })}</span>
              </p>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

/** Where the invoice stands with the tax office, and its e-invoice. */
function FiscalBox({ d, f }: { d: Detail; f: Fiscal }) {
  const e = f.einvoice;
  const tone: Tone = f.state === 'rejected' || e?.state === 'rejected' ? 'danger' : f.state === 'fiscalized' && (!e || e.state === 'sent') ? 'ok' : 'warn';
  const title =
    f.state === 'rejected'
      ? t('inv.fiscal_rejected')
      : f.state === 'pending'
        ? t('inv.fiscal_pending')
        : !e
          ? t('inv.fiscal_done')
          : e.state === 'sent'
            ? t('inv.fiscal_einvoice_sent')
            : e.state === 'rejected'
              ? t('inv.fiscal_einvoice_rejected')
              : t('inv.fiscal_einvoice_pending');
  const error = f.state === 'rejected' ? f.last_error : e?.state === 'rejected' ? e.last_error : null;
  const download = () => saveBlob(new Blob([e!.xml!], { type: 'application/xml' }), `${docName(d.invoice).replace(/[ /]/g, '-')}.xml`);
  return (
    <div className={`callout callout-${tone === 'ok' ? 'ok' : tone === 'danger' ? 'danger' : 'warn'} inv-fiscal`}>
      <Icon name={tone === 'ok' ? 'checkCircle' : tone === 'danger' ? 'alert' : 'clock'} size={16} />
      <div className="inv-fiscal-text">
        <strong>{title}</strong>
        {e?.eic ? <span className="mono">{t('inv.fiscal_eic', { eic: e.eic })}</span> : null}
        {error ? <span>{error}</span> : null}
        {f.state === 'rejected' || e?.state === 'rejected' ? <span>{t('inv.fiscal_fix_hint')}</span> : null}
        {!e && f.state === 'fiscalized' ? <span>{t('inv.fiscal_no_einvoice')}</span> : null}
      </div>
      {e?.xml ? (
        <Button size="sm" icon="download" onClick={download}>
          {t('inv.fiscal_download')}
        </Button>
      ) : null}
    </div>
  );
}

function PayBox({ d, onDone, onCancel }: { d: Detail; onDone: () => Promise<void>; onCancel: () => void }) {
  if (d.invoice.currency) return <FxPaymentForm d={d} onDone={onDone} onCancel={onCancel} />;
  const inv = d.invoice;
  const owed = left(inv);
  const discount = inv.discount_amount && inv.discount_until && inv.paid === 0 ? { amount: inv.discount_amount, until: inv.discount_until, percent: inv.discount_percent ?? 0 } : undefined;
  return <PaymentForm owed={owed} target={{ invoice: inv.id }} name={d.customer.name} discount={discount} onDone={onDone} onCancel={onCancel} />;
}

/** The instalments of an invoice: what is paid, what is due next, what is late. */
function InstalmentList({ inv }: { inv: InvoiceRow }) {
  const today = todayIso();
  let sum = 0;
  return (
    <section aria-labelledby="inv-instalments">
      <h3 id="inv-instalments" className="field-label">
        {t('terms.instalments')}
      </h3>
      <ul className="terms-list">
        {inv.schedule!.map((s, i) => {
          sum += s.amount;
          const paid = sum <= inv.paid;
          const part = !paid && sum - s.amount < inv.paid;
          const late = !paid && s.due < today && inv.state === 'issued';
          return (
            <li key={i} className={paid ? 'paid' : late ? 'late' : ''}>
              <span>{t('terms.nth', { n: i + 1 })}</span>
              <span>{day(s.due)}</span>
              <strong>{money(s.amount)}</strong>
              <StatusBadge
                tone={paid ? 'ok' : late ? 'danger' : part ? 'warn' : 'neutral'}
                icon={paid ? 'check' : late ? 'alert' : 'clock'}
                label={paid ? t('terms.st_paid') : late ? t('terms.st_late') : part ? t('terms.st_part') : t('terms.st_due')}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Money in for an invoice in another currency: the amount in that currency and the rate of the
 * day it came in. What it pays off is at the invoice's rate; the rest is an exchange difference.
 */
function FxPaymentForm({ d, onDone, onCancel }: { d: Detail; onDone: () => Promise<void>; onCancel: () => void }) {
  const inv = d.invoice;
  const code = inv.currency!;
  const { busy, run } = useSave();
  const owed = fxLeft(inv);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<Method>('bank');
  const [reference, setReference] = useState('');
  const [paidOn, setPaidOn] = useState(todayIso());
  const [rateTyped, setRateTyped] = useState('');
  const dayRate = useDayRate(code, paidOn);
  const rate = effectiveRate(rateTyped, dayRate);
  const n = amount === '' ? owed : parseMoney(amount);
  const applied = n ? (n === owed ? left(inv) : Math.min(toBase(n, inv.rate ?? 0), left(inv))) : 0;
  const received = n && rate ? toBase(n, rate) : null;
  const diff = received === null ? null : received - applied;
  const pay = () =>
    void run(async () => {
      await action('invoicing.pay', { invoice: inv.id, fx_amount: n, rate: rateTyped.trim() ? Number(rateTyped.trim().replace(',', '.')) : null, method, reference: reference.trim() || null, paid_on: paidOn });
      await onDone();
    }, t('inv.paid_toast', { amount: moneyIn(n ?? 0, code), name: d.customer.name }));
  return (
    <section className="pay-box" aria-labelledby="inv-pay-title">
      <h3 id="inv-pay-title">{t('inv.record_payment')}</h3>
      <div className="segmented" role="radiogroup" aria-label={t('inv.pay_method')}>
        {METHODS.map((m) => (
          <button key={m.key} role="radio" aria-checked={method === m.key} className={method === m.key ? 'active' : ''} onClick={() => setMethod(m.key)}>
            <Icon name={m.icon} size={16} /> {t(`inv.pm_${m.key}`)}
          </button>
        ))}
      </div>
      <div className="form-grid">
        <Field label={t('fx.amount_in', { code })} hint={t('inv.amount_hint', { owed: moneyIn(owed, code) })} error={n === null ? t('inv.money_rule') : n > owed ? t('inv.too_much', { owed: moneyIn(owed, code) }) : null}>
          <input className="input" inputMode="decimal" value={amount} placeholder={priceText(owed)} onChange={(e) => setAmount(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('inv.paid_on')}>
          <input className="input" type="date" value={paidOn} max={todayIso()} onChange={(e) => setPaidOn(e.target.value)} />
        </Field>
        <Field
          label={t('fx.rate_label', { code, base: d.base_currency ?? '' })}
          hint={dayRate && !rateTyped.trim() ? t('fx.day_rate_hint', { date: day(dayRate.date) }) : t('fx.bank_rate_hint')}
          error={!rateTyped.trim() && dayRate === null ? t('fx.no_rate_day', { code, date: day(paidOn) }) : null}
        >
          <input className="input mono" inputMode="decimal" value={rateTyped} placeholder={dayRate ? String(dayRate.rate / 1_000_000) : ''} onChange={(e) => setRateTyped(e.target.value)} />
        </Field>
        <Field label={t('inv.pay_reference')} hint={t('inv.pay_reference_hint')}>
          <input className="input" value={reference} maxLength={80} onChange={(e) => setReference(e.target.value)} />
        </Field>
      </div>
      {n && received !== null && diff !== null ? (
        <div className="fx-diff-preview" role="status">
          <span>
            {t('fx.pays_off')} <strong>{money(applied)}</strong>
          </span>
          <span>
            {t('fx.received')} <strong>{money(received)}</strong>
          </span>
          {diff ? (
            <span className={diff > 0 ? 'fx-gain' : 'fx-loss'}>
              {t(diff > 0 ? 'fx.gain_label' : 'fx.loss_label')} <strong>{money(Math.abs(diff))}</strong>
            </span>
          ) : (
            <span className="muted">{t('fx.no_difference')}</span>
          )}
        </div>
      ) : null}
      <div className="form-actions">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" icon="check" onClick={pay} busy={busy} disabled={!n || n > owed || !paidOn || rate === null}>
          {t('inv.pay_amount', { amount: moneyIn(n ?? 0, code) })}
        </Button>
      </div>
    </section>
  );
}

/** Money in: for one invoice, or for a customer (it pays their oldest invoices first). */
function PaymentForm({
  owed,
  target,
  name,
  discount,
  onDone,
  onCancel,
}: {
  owed: number;
  target: { invoice: string } | { customer: string };
  name: string;
  /** The invoice's early-payment discount, while nothing is paid on it. */
  discount?: { amount: number; until: string; percent: number };
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const { busy, run } = useSave();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<Method>('bank');
  const [reference, setReference] = useState('');
  const [paidOn, setPaidOn] = useState(todayIso());
  const [takeDiscount, setTakeDiscount] = useState(true);
  const early = !!discount && paidOn <= discount.until && takeDiscount;
  const n = early ? owed - discount!.amount : amount === '' ? owed : parseMoney(amount);
  const pay = () =>
    void run(async () => {
      await action('invoicing.pay', { ...target, amount: n, method, reference: reference.trim() || null, paid_on: paidOn, ...(early ? { early_discount: true } : {}) });
      await onDone();
    }, t('inv.paid_toast', { amount: money(n ?? 0), name }));
  return (
    <section className="pay-box" aria-labelledby="inv-pay-title">
      <h3 id="inv-pay-title">{t('inv.record_payment')}</h3>
      <div className="segmented" role="radiogroup" aria-label={t('inv.pay_method')}>
        {METHODS.map((m) => (
          <button key={m.key} role="radio" aria-checked={method === m.key} className={method === m.key ? 'active' : ''} onClick={() => setMethod(m.key)}>
            <Icon name={m.icon} size={16} /> {t(`inv.pm_${m.key}`)}
          </button>
        ))}
      </div>
      {discount && paidOn <= discount.until ? (
        <div className="switch-row">
          <span>
            <span className="switch-title">{t('terms.take_discount', { pct: discount.percent, amount: money(discount.amount) })}</span>
            <span className="muted small">{t('terms.take_discount_hint', { date: day(discount.until) })}</span>
          </span>
          <Switch checked={takeDiscount} label={t('terms.take_discount', { pct: discount.percent, amount: money(discount.amount) })} onChange={setTakeDiscount} />
        </div>
      ) : null}
      <div className="form-grid">
        <Field
          label={t('inv.amount')}
          hint={early ? t('terms.amount_with_discount', { owed: money(owed) }) : 'customer' in target ? t('inv.amount_oldest', { owed: money(owed) }) : t('inv.amount_hint', { owed: money(owed) })}
          error={n === null ? t('inv.money_rule') : n > owed ? t('inv.too_much', { owed: money(owed) }) : null}
        >
          <input className="input" inputMode="decimal" value={early ? priceText(n!) : amount} disabled={early} placeholder={priceText(owed)} onChange={(e) => setAmount(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('inv.paid_on')}>
          <input className="input" type="date" value={paidOn} max={todayIso()} onChange={(e) => setPaidOn(e.target.value)} />
        </Field>
        <Field label={t('inv.pay_reference')} hint={t('inv.pay_reference_hint')}>
          <input className="input" value={reference} maxLength={80} onChange={(e) => setReference(e.target.value)} />
        </Field>
      </div>
      <div className="form-actions">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" icon="check" onClick={pay} busy={busy} disabled={!n || n > owed || !paidOn}>
          {t('inv.pay_amount', { amount: money(n ?? 0) })}
        </Button>
      </div>
    </section>
  );
}

/** A credit note: the whole invoice, or some of each line (goods sent back, a lower price agreed). */
function CreditBox({ d, onCancel, onDone }: { d: Detail; onCancel: () => void; onDone: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const remaining = d.lines.map((l) => Math.round((l.qty - l.credited) * 1000) / 1000);
  const [whole, setWhole] = useState(d.lines.every((l) => l.credited === 0));
  const [qtys, setQtys] = useState(remaining.map(qtyText));
  const [reason, setReason] = useState('');
  const [returnGoods, setReturnGoods] = useState(false);
  const goods = d.lines.some((l) => l.product);
  const picked = d.lines.map((l, i) => ({ l, max: remaining[i]!, qty: parseNumber(qtys[i]!) }));
  const bad = !whole && picked.some((p) => p.qty === null || p.qty < 0 || p.qty > p.max);
  const chosen = whole ? picked.map((p) => ({ ...p, qty: p.max })) : picked.filter((p) => (p.qty ?? 0) > 0);
  const cur = d.invoice.currency ?? null;
  const unit = (l: Line) => (cur ? (l.fx_unit_price ?? l.unit_price) : l.unit_price);
  const amount = chosen.reduce((s, p) => s + lineFigures(unit(p.l), p.qty!, p.l.discount, p.l.tax_rate).total, 0);
  const go = () =>
    void run(async () => {
      const cn = await action<{ id: string }>('invoicing.credit', {
        invoice: d.invoice.id,
        reason: reason.trim(),
        return_goods: goods && returnGoods,
        lines: whole ? undefined : chosen.map((p) => ({ line: p.l.id, qty: p.qty })),
      });
      await onDone(cn.id);
    }, t('inv.credited', { amount: moneyIn(amount, cur) }));
  return (
    <section className="pay-box" aria-labelledby="inv-credit-title">
      <h3 id="inv-credit-title">{t('inv.credit_note')}</h3>
      <p className="muted">{t('inv.credit_hint')}</p>
      <div className="segmented" role="radiogroup" aria-label={t('inv.credit_what')}>
        <button role="radio" aria-checked={whole} className={whole ? 'active' : ''} onClick={() => setWhole(true)}>
          {t('inv.credit_all')}
        </button>
        <button role="radio" aria-checked={!whole} className={!whole ? 'active' : ''} onClick={() => setWhole(false)}>
          {t('inv.credit_some')}
        </button>
      </div>
      {!whole ? (
        <div className="list-card">
          {picked.map((p, i) => (
            <div key={p.l.id} className="list-row compact-move">
              <span className="list-main">
                <span className="list-title">{p.l.name}</span>
                <span className="list-sub">{t('inv.can_credit', { qty: qtyText(p.max), unit: t(`inv.u_${p.l.unit}` as MessageKey), price: moneyIn(unit(p.l), cur) })}</span>
              </span>
              <input
                className={`input inv-credit-qty${p.qty === null || (p.qty ?? 0) > p.max ? ' invalid' : ''}`}
                inputMode="decimal"
                aria-label={t('inv.credit_qty', { name: p.l.name })}
                value={qtys[i]}
                disabled={p.max <= 0}
                onChange={(e) => setQtys(qtys.map((q, j) => (j === i ? e.target.value : q)))}
              />
            </div>
          ))}
        </div>
      ) : null}
      <Field label={t('inv.credit_reason_label')} required hint={t('inv.credit_reason_hint')}>
        <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} data-autofocus />
      </Field>
      {goods ? (
        <label className="switch-row">
          <span>
            <span className="switch-title">{t('inv.return_goods')}</span>
            <span className="muted">{t('inv.return_goods_hint')}</span>
          </span>
          <Switch checked={returnGoods} label={t('inv.return_goods')} onChange={setReturnGoods} />
        </label>
      ) : null}
      <div className="form-actions">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" icon="undo" onClick={go} busy={busy} disabled={bad || !chosen.length || amount === 0 || reason.trim().length < 3}>
          {t('inv.issue_credit', { amount: moneyIn(amount, cur) })}
        </Button>
      </div>
    </section>
  );
}

/** What a credit note has left pays another open invoice of the same customer. */
function ApplyCreditBox({ d, onCancel, onDone }: { d: Detail; onCancel: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data } = useAction<InvoiceRow[]>('invoicing.invoices', { filter: 'open', customer: d.invoice.customer });
  const open = (data ?? []).filter((r) => r.kind === 'invoice' && r.state === 'issued' && (r.currency ?? null) === (d.invoice.currency ?? null));
  const [pick, setPick] = useState('');
  const go = () =>
    void run(async () => {
      await action('invoicing.apply_credit', { credit_note: d.invoice.id, invoice: pick });
      await onDone();
    }, t('inv.credit_used'));
  return (
    <section className="pay-box" aria-labelledby="inv-apply-title">
      <h3 id="inv-apply-title">{t('inv.use_credit')}</h3>
      {!data ? (
        <Skeleton h={80} />
      ) : !open.length ? (
        <p className="muted">{t('inv.no_open_invoices', { name: d.customer.name })}</p>
      ) : (
        <Field label={t('inv.pay_which')} hint={t('inv.use_credit_hint', { amount: d.invoice.currency ? moneyIn(Math.abs(fxLeft(d.invoice)), d.invoice.currency) : money(Math.abs(left(d.invoice))) })}>
          <select className="input" value={pick} onChange={(e) => setPick(e.target.value)} data-autofocus>
            <option value="">{t('inv.pick_invoice')}</option>
            {open.map((r) => (
              <option key={r.id} value={r.id}>
                {docName(r)} · {t('inv.left', { amount: r.currency ? moneyIn(fxLeft(r), r.currency) : money(left(r)) })}
              </option>
            ))}
          </select>
        </Field>
      )}
      <div className="form-actions">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" icon="check" onClick={go} busy={busy} disabled={!pick}>
          {t('inv.use_credit')}
        </Button>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- The printed invoice */

/** The A4 page: seller, buyer, lines, VAT by rate, how to pay. */
export function InvoicePaper({ d, look, sampleFields }: { d: Detail; look?: PaperLook | null; /** Made-up fields, for the template preview. */ sampleFields?: { label: string; text: string }[] }) {
  const inv = d.invoice;
  const c = d.company;
  const buyer = inv.buyer ?? { name: d.customer.name, tax_id: d.customer.tax_id, id_type: d.customer.id_type, address: d.customer.address, town: d.customer.town, country: d.customer.country };
  const credit = inv.kind === 'credit';
  const original = credit ? d.related.find((r) => r.id === inv.credit_of) : undefined;
  const L = look?.look ?? DEFAULT_LOOK;
  const discounts = d.lines.some((l) => l.discount);
  const by = L.show.issued_by ? inv.issued_by_name : null;
  // In another currency the paper shows its figures, with the rate and the total in the business's currency.
  const cur = inv.currency ?? null;
  const pm = (n: number) => moneyIn(n, cur);
  const fxLines = cur ? d.lines.map((l) => ({ ...l, net: l.fx_net ?? l.net, tax: l.fx_tax ?? l.tax })) : d.lines;
  return (
    <article {...paperAttrs(look)} aria-label={docName(inv)}>
      <header className="inv-paper-head">
        <PaperSeller c={c} p={look} />
        <div className="inv-paper-title">
          <PaperLogoRight p={look} />
          <h2>{credit ? t('inv.p_credit_note') : t('inv.p_invoice')}</h2>
          <p className="inv-number">{inv.name ? `${inv.name}` : t('inv.draft')}</p>
          <dl>
            <dt>{t('inv.date')}</dt>
            <dd>{day(inv.date)}</dd>
            {!credit && !inv.schedule?.length ? (
              <>
                <dt>{t('inv.due_date')}</dt>
                <dd>{day(inv.due_date)}</dd>
              </>
            ) : null}
            {inv.reference ? (
              <>
                <dt>{t('inv.reference')}</dt>
                <dd>{inv.reference}</dd>
              </>
            ) : null}
            {d.order ? (
              <>
                <dt>{t('inv.p_order')}</dt>
                <dd>
                  {d.order.number}/{d.order.year}
                </dd>
              </>
            ) : null}
            {original ? (
              <>
                <dt>{t('inv.p_corrects')}</dt>
                <dd>
                  {original.number}/{original.year} · {day(original.date)}
                </dd>
              </>
            ) : null}
          </dl>
        </div>
      </header>
      <section className="inv-paper-parties">
        <div>
          <p className="inv-label">{t('inv.p_buyer')}</p>
          <p className="strong">{buyer.name}</p>
          {buyer.tax_id ? <p>{t('inv.p_id', { type: buyerIdLabel(buyer), id: buyer.tax_id })}</p> : null}
          <p>{[buyer.address, buyer.town, buyer.country !== c.country ? countryName(buyer.country) : null].filter(Boolean).join(', ')}</p>
        </div>
        {d.branch.name ? (
          <div>
            <p className="inv-label">{t('inv.p_issued_at')}</p>
            <p>{d.branch.name}</p>
            <p>{[d.branch.address, d.branch.city].filter(Boolean).join(', ')}</p>
          </div>
        ) : null}
      </section>
      <PaperFields p={look} of={[['invoicing.invoice', inv.id], ['customer.customer', inv.customer]]} sample={sampleFields} />
      <table className="inv-paper-lines">
        <thead>
          <tr>
            {L.lines.number ? <th>#</th> : null}
            <th>{t('inv.description')}</th>
            <th className="num">{t('inv.qty')}</th>
            <th className="num">{t('inv.unit_price')}</th>
            {discounts ? <th className="num">{t('inv.discount')}</th> : null}
            <th className="num">{t('inv.vat')}</th>
            <th className="num">{t('inv.net')}</th>
          </tr>
        </thead>
        <tbody>
          {d.lines.map((l, i) => (
            <tr key={l.id}>
              {L.lines.number ? <td>{i + 1}</td> : null}
              <td>{l.name}</td>
              <td className="num">
                {qtyText(l.qty)}
                {L.lines.unit ? ` ${t(`inv.u_${l.unit}` as MessageKey)}` : ''}
              </td>
              <td className="num">{pm(cur ? (l.fx_unit_price ?? l.unit_price) : l.unit_price)}</td>
              {discounts ? <td className="num">{l.discount ? `${l.discount} %` : ''}</td> : null}
              <td className="num">{l.tax_rate} %</td>
              <td className="num">{pm(cur ? (l.fx_net ?? l.net) : l.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="inv-paper-foot">
        <div className="inv-paper-pay">
          {credit && inv.reason ? <p>{t('inv.credit_reason', { reason: inv.reason })}</p> : null}
          {!credit && L.show.bank && d.settings.bank_accounts.length ? (
            <>
              <p className="inv-label">{t('inv.p_pay_to')}</p>
              {d.settings.bank_accounts.map((b) => (
                <p key={b.iban}>
                  {b.bank} · {b.currency} · <span className="mono">{b.iban}</span>
                  {b.swift ? ` · SWIFT ${b.swift}` : ''}
                </p>
              ))}
            </>
          ) : null}
          {!credit && inv.schedule?.length ? (
            <>
              <p className="inv-label">{t('terms.p_instalments')}</p>
              {inv.schedule.map((x, i) => (
                <p key={i}>
                  {t('terms.p_instalment', { n: i + 1, date: day(x.due), amount: pm(cur && inv.total ? Math.round((x.amount * (inv.fx_total ?? 0)) / inv.total) : x.amount) })}
                </p>
              ))}
            </>
          ) : null}
          {!credit && inv.discount_amount && inv.discount_until ? <p>{t('terms.p_discount', { pct: inv.discount_percent ?? 0, date: day(inv.discount_until), amount: pm(inv.total - inv.discount_amount) })}</p> : null}
          {inv.note ? <p>{inv.note}</p> : null}
          {!credit && L.show.terms && d.settings.terms ? <p className="muted">{d.settings.terms}</p> : null}
          {invoiceLegalNotes(buyer, inv, PACK_GROUPS.eu).map((n) => (
            <p key={n} className="muted inv-legal">
              {n}
            </p>
          ))}
        </div>
        {cur ? (
          <div>
            <Totals net={inv.fx_net ?? 0} tax={inv.fx_tax ?? 0} rates={byRate(fxLines)} currency={cur} base={{ total: inv.total, rate: inv.rate ?? 0, code: d.base_currency ?? '' }} />
            <p className="inv-paper-fx">{t('fx.paper_vat', { net: money(inv.net), tax: money(inv.tax), rate: rateText(inv.rate ?? 0), code: cur, base: d.base_currency ?? '' })}</p>
          </div>
        ) : (
          <Totals net={inv.net} tax={inv.tax} rates={byRate(d.lines)} />
        )}
      </div>
      {d.fiscal ? <PaperFiscal f={d.fiscal} /> : null}
      {by && !L.show.signatures ? <p className="inv-paper-by muted">{t('inv.p_issued_by', { name: by })}</p> : null}
      <PaperEnd p={look} by={by} />
    </article>
  );
}

/** The fiscal codes printed on the invoice, with the QR code to check it with the tax office. */
function PaperFiscal({ f }: { f: Fiscal }) {
  const rows: [MessageKey, string | null][] = [
    ['inv.p_fiscal_number', f.inv_num],
    ['inv.p_iic', f.iic],
    ['inv.p_fic', f.fic ?? t('inv.p_fic_waiting')],
    ['inv.p_eic', f.einvoice?.eic ?? null],
    ['inv.p_corrects_iic', f.corrects],
    ['inv.p_operator', f.operator],
    ['inv.p_business_unit', f.business_unit],
    ['inv.p_tcr', f.tcr],
    ['inv.p_software', f.software],
  ];
  return (
    <section className="inv-paper-fiscal" aria-label={t('inv.p_fiscal')}>
      <QrCode rows={f.qr} label={t('inv.p_fiscal_qr')} />
      <div>
        {f.test ? <p className="inv-paper-test">{t('inv.p_fiscal_test')}</p> : null}
        <dl>
          {rows
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <Fragment key={k}>
                <dt>{t(k)}</dt>
                <dd className="mono">{v}</dd>
              </Fragment>
            ))}
        </dl>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Customers */

function Customers({
  onChange,
  onOpenInvoice,
  onNewInvoice,
  onOpenQuote,
  onNewQuote,
}: {
  onChange: () => Promise<void>;
  onOpenInvoice: (id: string) => void;
  onNewInvoice: (customer: string) => void;
  onOpenQuote: (id: string) => void;
  onNewQuote: (customer: string) => void;
}) {
  const [filter, setFilter] = useState<'all' | 'owing' | 'overdue' | 'archived'>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const { data, reload } = useAction<Customer[]>('invoicing.customers', { filter, search: q || undefined });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="radiogroup" aria-label={t('inv.show')}>
          {(['all', 'owing', 'overdue', 'archived'] as const).map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {t(`inv.cf_${f}`)}
            </button>
          ))}
        </div>
        <form
          className="search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(search.trim());
          }}
        >
          <Icon name="search" size={18} />
          <input className="input" placeholder={t('inv.search_customers')} aria-label={t('inv.search_customers')} value={search} onChange={(e) => setSearch(e.target.value)} onBlur={() => setQ(search.trim())} />
          {search ? <IconButton icon="x" label={t('common.clear')} onClick={() => (setSearch(''), setQ(''))} /> : null}
        </form>
      </div>
      <section className="list-card" aria-label={t('inv.tab_customers')}>
        {!data ? (
          <Skeleton h={160} />
        ) : data.length === 0 ? (
          <EmptyState icon="users" title={q ? t('order.no_results') : t('inv.no_customers')} body={q ? undefined : t('inv.no_customers_body')} />
        ) : (
          data.map((c) => (
            <button key={c.id} className={`list-row stock-row${c.active ? '' : ' archived'}`} onClick={() => setOpen(c.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name={c.is_company ? 'store' : 'users'} size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">{c.name}</span>
                <span className="list-sub">{customerHint({ ...c, open: 0 })}</span>
              </span>
              <span className="stock-qty">
                <strong className={c.overdue ? 'danger' : ''}>{money(c.open ?? 0)}</strong>
                <span className="muted">{c.overdue ? t('inv.overdue_amount', { amount: money(c.overdue) }) : t('inv.owed')}</span>
              </span>
              {c.overdue ? <StatusBadge tone="danger" icon="alert" label={t('inv.st_overdue')} /> : null}
            </button>
          ))
        )}
      </section>
      {open ? (
        <CustomerCard
          key={open}
          id={open}
          onClose={() => setOpen(null)}
          onChange={async () => {
            await Promise.all([reload(), onChange()]);
          }}
          onOpenInvoice={(id) => (setOpen(null), onOpenInvoice(id))}
          onNewInvoice={(c) => (setOpen(null), onNewInvoice(c))}
          onOpenQuote={(id) => (setOpen(null), onOpenQuote(id))}
          onNewQuote={(c) => (setOpen(null), onNewQuote(c))}
        />
      ) : null}
    </>
  );
}

interface CustomerDetail {
  customer: Customer & { problems: string[]; note: string | null; pricelist_active: boolean };
  balance: { open: number; overdue: number };
  sales: { year_net: number; last_year_net: number };
  documents: InvoiceRow[];
  quotes: (Pick<QuoteRow, 'id' | 'number' | 'year' | 'state' | 'date' | 'valid_until' | 'total' | 'confirmed_at' | 'expired'>)[];
}

/** EU countries whose VAT numbers VIES checks (Northern Ireland trades goods as XI). */
const VIES = [...PACK_GROUPS.eu, 'XI'] as readonly string[];

/** Whether the customer's EU VAT number is registered for trade across the EU, as VIES last said. */
function ViesCheck({ customer: c, canCheck, onChecked }: { customer: Customer; canCheck: boolean; onChecked: () => Promise<void> }) {
  const { busy, run } = useSave();
  const check = () =>
    void run(async () => {
      await action('invoicing.check_vat', { customer: c.id });
      await onChecked();
    });
  return (
    <span className="vies-check">
      {c.vies_state === 'valid' ? (
        <StatusBadge tone="ok" icon="checkCircle" label={t('inv.vies_valid')} />
      ) : c.vies_state === 'invalid' ? (
        <StatusBadge tone="danger" icon="alert" label={t('inv.vies_invalid')} />
      ) : (
        <StatusBadge tone="neutral" icon="help" label={t('inv.vies_unchecked')} />
      )}
      {c.vies_checked_at ? <span className="muted small">{t('inv.vies_on', { date: day(c.vies_checked_at.slice(0, 10)) })}</span> : null}
      {c.vies_name ? <span className="small">{c.vies_name}</span> : null}
      {canCheck ? (
        <Button size="sm" icon="shieldCheck" busy={busy} onClick={check}>
          {c.vies_checked_at ? t('inv.vies_again') : t('inv.vies_check')}
        </Button>
      ) : null}
    </span>
  );
}

function CustomerCard({
  id,
  onClose,
  onChange,
  onOpenInvoice,
  onNewInvoice,
  onOpenQuote,
  onNewQuote,
}: {
  id: string;
  onClose: () => void;
  onChange: () => Promise<void>;
  onOpenInvoice: (id: string) => void;
  onNewInvoice: (customer: string) => void;
  onOpenQuote: (id: string) => void;
  onNewQuote: (customer: string) => void;
}) {
  const { can } = useSession();
  const { data: d, reload } = useAction<CustomerDetail>('invoicing.customer', { customer: id });
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [statement, setStatement] = useState(false);
  const refresh = async () => {
    await Promise.all([reload(), onChange()]);
  };
  if (statement && d) return <StatementDialog customer={d.customer.id} name={d.customer.name} onClose={() => setStatement(false)} />;
  if (editing && d) return <CustomerEditor customer={d.customer} onClose={() => setEditing(false)} onSaved={async () => (setEditing(false), await refresh())} />;
  const c = d?.customer;
  const owedInvoices = d ? d.documents.filter((x) => x.kind === 'invoice' && x.state === 'issued').reduce((s, x) => s + left(x), 0) : 0;
  return (
    <Dialog
      title={c?.name ?? t('inv.customer')}
      onClose={onClose}
      size="lg"
      footer={
        c ? (
          <>
            <span className="foot-actions">
              {can('invoicing.manage') ? (
                <Button icon="edit" onClick={() => setEditing(true)}>
                  {t('inv.edit_customer')}
                </Button>
              ) : null}
              <Button icon="list" onClick={() => setStatement(true)}>
                {t('st.button')}
              </Button>
              {can('invoicing.payments') && owedInvoices > 0 ? (
                <Button icon="cash" onClick={() => setPaying(true)}>
                  {t('inv.record_payment')}
                </Button>
              ) : null}
              {can('invoicing.manage') && c.active ? (
                <Button icon="note" onClick={() => onNewQuote(c.id)}>
                  {t('quote.new')}
                </Button>
              ) : null}
            </span>
            {can('invoicing.manage') && c.active ? (
              <Button variant="primary" icon="plus" onClick={() => onNewInvoice(c.id)}>
                {t('inv.new_invoice')}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {!d || !c ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('inv.owed')}</span>
              <strong>{money(d.balance.open)}</strong>
            </div>
            <div>
              <span className="muted">{t('inv.k_overdue')}</span>
              <strong className={d.balance.overdue ? 'text-danger' : ''}>{money(d.balance.overdue)}</strong>
            </div>
            <div>
              <span className="muted">{t('inv.sales_year')}</span>
              <strong>{money(d.sales.year_net)}</strong>
            </div>
            {c.credit_limit !== null ? (
              <div>
                <span className="muted">{t('inv.credit_limit')}</span>
                <strong>{money(c.credit_limit)}</strong>
              </div>
            ) : null}
          </div>
          {c.problems.length ? (
            <div className="callout callout-warn" role="status">
              <Icon name="alert" size={20} />
              <div>
                <p className="strong">{t('inv.needs_details', { name: c.name })}</p>
                <p className="muted">{t('inv.missing_list', { list: c.problems.map((p) => t(`inv.miss_${p}` as MessageKey)).join(', ') })}</p>
              </div>
            </div>
          ) : null}
          <CustomFieldsView target="customer.customer" record={c.id} version={d} />
          <dl className="report kv">
            {c.tax_id ? (
              <>
                <dt>{buyerIdLabel(c)}</dt>
                <dd className="mono">{c.tax_id}</dd>
              </>
            ) : null}
            {c.tax_id && c.is_company && VIES.includes(c.country) ? (
              <>
                <dt>{t('inv.vies')}</dt>
                <dd>
                  <ViesCheck customer={c} canCheck={can('invoicing.manage')} onChecked={refresh} />
                </dd>
              </>
            ) : null}
            {c.address || c.town ? (
              <>
                <dt>{t('inv.address')}</dt>
                <dd>{[c.address, c.town, c.country].filter(Boolean).join(', ')}</dd>
              </>
            ) : null}
            {c.phone || c.email ? (
              <>
                <dt>{t('inv.contact')}</dt>
                <dd>{[c.phone, c.email].filter(Boolean).join(' · ')}</dd>
              </>
            ) : null}
            <dt>{t('inv.payment_days')}</dt>
            <dd>{c.payment_days ? tn('inv.pays_in', c.payment_days) : t('inv.pays_now')}</dd>
            {c.pricelist_name ? (
              <>
                <dt>{t('inv.pricelist')}</dt>
                <dd>{c.pricelist_active ? c.pricelist_name : t('inv.pricelist_archived', { name: c.pricelist_name })}</dd>
              </>
            ) : null}
            {c.note ? (
              <>
                <dt>{t('inv.note')}</dt>
                <dd>{c.note}</dd>
              </>
            ) : null}
          </dl>
          {paying ? <PaymentForm owed={owedInvoices} target={{ customer: c.id }} name={c.name} onCancel={() => setPaying(false)} onDone={async () => (setPaying(false), await refresh())} /> : null}
          {d.quotes.length ? (
            <section className="item-moves">
              <h3>{t('quote.open_for_customer')}</h3>
              <div className="list-card">
                {d.quotes.map((x) => (
                  <button key={x.id} className="list-row compact-move" onClick={() => onOpenQuote(x.id)}>
                    <span className="list-icon" aria-hidden="true">
                      <Icon name={x.confirmed_at ? 'bag' : 'note'} size={18} />
                    </span>
                    <span className="list-main">
                      <span className="list-title">{quoteName(x)}</span>
                      <span className="list-sub">{day(x.date)}</span>
                    </span>
                    <strong>{money(x.total)}</strong>
                    <QuoteBadge q={{ ...x, invoiced: 0 }} />
                  </button>
                ))}
              </div>
            </section>
          ) : null}
          <section className="item-moves">
            <h3>{t('inv.documents')}</h3>
            <div className="list-card">
              {d.documents.length ? (
                d.documents.map((x) => (
                  <button key={x.id} className="list-row compact-move" onClick={() => onOpenInvoice(x.id)}>
                    <span className="list-icon" aria-hidden="true">
                      <Icon name={x.kind === 'credit' ? 'undo' : 'invoice'} size={18} />
                    </span>
                    <span className="list-main">
                      <span className="list-title">{docName(x)}</span>
                      <span className="list-sub">{[day(x.date), x.reference].filter(Boolean).join(' · ')}</span>
                    </span>
                    <strong>{money(x.total)}</strong>
                    <InvoiceBadge d={x} />
                  </button>
                ))
              ) : (
                <p className="muted pad">{t('inv.no_documents')}</p>
              )}
            </div>
          </section>
          <RecordNotes model="customer.customer" record={c.id} version={d} />
        </div>
      )}
    </Dialog>
  );
}

const NIPT = /^[A-Z]\d{8}[A-Z]$/;

export function CustomerEditor({ customer, onClose, onSaved }: { customer?: Customer; onClose: () => void; onSaved: (c: Customer) => Promise<void> }) {
  const { busy, run } = useSave();
  const cf = useCustomFields('customer.customer', customer?.id);
  const [tried, setTried] = useState(false);
  const homeRules = useTaxRates().rules;
  const home = homeRules?.code ?? 'US';
  const [v, setV] = useState({
    name: customer?.name ?? '',
    is_company: customer?.is_company ?? true,
    tax_id: customer?.tax_id ?? '',
    id_type: customer?.id_type ?? '',
    address: customer?.address ?? '',
    town: customer?.town ?? '',
    country: customer?.country ?? home,
    phone: customer?.phone ?? '',
    email: customer?.email ?? '',
    payment_days: String(customer?.payment_days ?? 0),
    payment_term: customer?.payment_term ?? '',
    credit_limit: customer?.credit_limit === null || customer?.credit_limit === undefined ? '' : priceText(customer.credit_limit),
    note: customer?.note ?? '',
    pricelist: customer?.pricelist ?? '',
    einvoice_id: customer?.einvoice_id ?? '',
  });
  const { data: lists } = useAction<{ id: string; name: string; discount: number }[]>('invoicing.pricelists', { archived: false });
  const { data: terms } = usePaymentTerms();
  const tax = v.tax_id.trim().toUpperCase().replace(/\s/g, '');
  const country = v.country.trim().toUpperCase();
  // Albanian businesses have a NIPT; a business elsewhere has its country's tax number; a person an ID.
  const idType = v.id_type || (tax ? (v.is_company ? (country === 'AL' ? 'NUIS' : 'TAX') : 'ID') : '');
  const niptBad = !!tax && country === 'AL' && idType === 'NUIS' && !NIPT.test(tax);
  const days = /^\d{1,3}$/.test(v.payment_days.trim()) && Number(v.payment_days) <= 365 ? Number(v.payment_days) : null;
  const limit = v.credit_limit.trim() ? parseMoney(v.credit_limit) : undefined;
  const countryBad = !/^[A-Z]{2}$/.test(country);
  const companyMissing = v.is_company && (!tax || !v.address.trim() || !v.town.trim());
  const field = (k: keyof typeof v) => ({ value: v[k] as string, onChange: (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value }) });
  const save = () => {
    setTried(true);
    if (!cf.valid) return;
    void run(async () => {
      const saved = await action<Customer>('invoicing.save_customer', {
        customer: customer?.id,
        name: v.name.trim(),
        is_company: v.is_company,
        tax_id: tax || null,
        id_type: tax ? idType : null,
        address: v.address,
        town: v.town,
        country,
        phone: v.phone,
        email: v.email.trim() || null,
        payment_days: days,
        payment_term: v.payment_term || null,
        credit_limit: limit ?? null,
        note: v.note,
        pricelist: v.pricelist || null,
        einvoice_id: v.is_company ? v.einvoice_id.trim() || null : null,
      });
      await cf.save(saved.id);
      await onSaved(saved);
    }, customer ? t('inv.customer_saved', { name: v.name.trim() }) : t('inv.customer_added', { name: v.name.trim() }));
  };
  return (
    <Dialog
      title={customer ? customer.name : t('inv.new_customer')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={v.name.trim().length < 2 || niptBad || days === null || limit === null || countryBad}>
            {customer ? t('common.save') : t('inv.add_customer')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <label className="switch-row">
          <span>
            <span className="switch-title">{t('inv.is_company')}</span>
            <span className="muted">{t('inv.is_company_hint')}</span>
          </span>
          <Switch checked={v.is_company} label={t('inv.is_company')} onChange={(is_company) => setV({ ...v, is_company })} />
        </label>
        <div className="form-grid">
          <Field label={v.is_company ? t('inv.legal_name') : t('inv.person_name')} required>
            <input className="input" maxLength={160} {...field('name')} data-autofocus />
          </Field>
          <Field
            label={buyerIdLabel({ id_type: idType || (v.is_company ? null : 'ID'), country })}
            required={v.is_company}
            error={niptBad ? t('inv.nipt_rule') : null}
            ok={tax && !niptBad && idType === 'NUIS' ? t('inv.nipt_ok') : null}
            hint={v.is_company ? (country === homeRules?.code ? idHint(homeRules.taxId) : t('business.tax_id_hint_other')) : t('inv.nipt_hint')}
          >
            <input className="input mono" maxLength={30} {...field('tax_id')} />
          </Field>
          <Field label={t('inv.address')} required={v.is_company}>
            <input className="input" maxLength={160} {...field('address')} />
          </Field>
          <Field label={t('inv.town')} required={v.is_company}>
            <input className="input" maxLength={80} {...field('town')} />
          </Field>
          <Field label={t('inv.country')} error={countryBad ? t('inv.country_rule') : null}>
            <CountrySelect value={v.country} onChange={(country) => setV({ ...v, country })} />
          </Field>
          <Field label={t('inv.id_type')} hint={t('inv.id_type_hint')}>
            <select className="input" value={v.id_type} onChange={(e) => setV({ ...v, id_type: e.target.value as never })}>
              <option value="">{t('inv.id_auto')}</option>
              {ID_TYPES.map((x) => (
                <option key={x} value={x}>
                  {t(`inv.id_${x}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('inv.phone')}>
            <input className="input" type="tel" maxLength={40} {...field('phone')} />
          </Field>
          <Field label={t('inv.email')}>
            <input className="input" type="email" maxLength={120} {...field('email')} />
          </Field>
          {v.is_company ? (
            <Field label={t('einv.customer_id')} hint={t(country === 'IT' ? 'einv.customer_id_it' : 'einv.customer_id_hint')}>
              <input className="input mono" maxLength={120} spellCheck={false} {...field('einvoice_id')} />
            </Field>
          ) : null}
          {terms?.length || v.payment_term ? (
            <Field label={t('terms.field')} hint={t('terms.customer_hint')}>
              <TermSelect terms={terms} value={v.payment_term} onChange={(id) => setV({ ...v, payment_term: id })} noneLabel={t('terms.none_days')} />
            </Field>
          ) : null}
          {v.payment_term ? null : (
            <Field label={t('inv.payment_days')} hint={t('inv.payment_days_hint')} error={days === null ? t('inv.days_rule') : null}>
              <input className="input" inputMode="numeric" maxLength={3} {...field('payment_days')} />
            </Field>
          )}
          <Field label={t('inv.credit_limit')} hint={t('inv.credit_limit_hint')} error={limit === null ? t('inv.money_rule') : null}>
            <input className="input" inputMode="decimal" {...field('credit_limit')} />
          </Field>
          <Field label={t('inv.pricelist')} hint={t('inv.pricelist_hint')}>
            <select className="input" {...field('pricelist')}>
              <option value="">{t('inv.pricelist_none')}</option>
              {(lists ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                  {l.discount ? ` · −${l.discount} %` : ''}
                </option>
              ))}
              {customer?.pricelist && lists && !lists.some((l) => l.id === customer.pricelist) ? <option value={customer.pricelist}>{customer.pricelist_name}</option> : null}
            </select>
          </Field>
        </div>
        <Field label={t('inv.note')}>
          <input className="input" maxLength={1000} {...field('note')} />
        </Field>
        {companyMissing ? (
          <p className="callout callout-info">
            <Icon name="alert" size={16} />
            <span>{t('inv.company_needs')}</span>
          </p>
        ) : null}
        <CustomFieldInputs cf={cf} tried={tried} />
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Settings */

function SettingsTab() {
  const { data } = useAction<Settings>('invoicing.settings');
  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  return (
    <>
      <SettingsForm initial={data} />
      <PaymentTermsPanel />
    </>
  );
}

function SettingsForm({ initial }: { initial: Settings }) {
  const { busy, run } = useSave();
  const [days, setDays] = useState(String(initial.payment_days));
  const { data: termList } = usePaymentTerms();
  const [term, setTerm] = useState(initial.payment_term ?? '');
  const [levels, setLevels] = useState(() => draftLevels(initial.reminder_levels ?? []));
  const [auto, setAuto] = useState(!!initial.reminders_auto);
  const levelsBad = levelsError(levels) !== null;
  const [quoteDays, setQuoteDays] = useState(String(initial.quote_days));
  const quoteDaysN = /^\d{1,3}$/.test(quoteDays.trim()) && Number(quoteDays) >= 1 && Number(quoteDays) <= 365 ? Number(quoteDays) : null;
  const [terms, setTerms] = useState(initial.terms ?? '');
  const [banks, setBanks] = useState<{ bank: string; iban: string; swift: string; currency: string }[]>(initial.bank_accounts.map((b) => ({ ...b, swift: b.swift ?? '' })));
  const daysN = /^\d{1,3}$/.test(days.trim()) && Number(days) <= 365 ? Number(days) : null;
  const ibanBad = (s: string) => !/^[A-Z]{2}\d{2}[A-Z0-9]{8,30}$/.test(s.toUpperCase().replace(/\s/g, ''));
  const bad = banks.some((b) => b.bank.trim().length < 2 || ibanBad(b.iban) || !/^[A-Za-z]{3}$/.test(b.currency.trim()));
  const set = (i: number, k: 'bank' | 'iban' | 'swift' | 'currency', value: string) => setBanks(banks.map((b, j) => (j === i ? { ...b, [k]: value } : b)));
  const save = () =>
    void run(
      () =>
        action('invoicing.save_settings', {
          payment_days: daysN,
          payment_term: term || null,
          reminder_levels: savedLevels(levels),
          reminders_auto: auto,
          quote_days: quoteDaysN,
          terms,
          bank_accounts: banks.map((b) => ({ bank: b.bank, iban: b.iban, swift: b.swift, currency: b.currency })),
        }),
      t('inv.settings_saved'),
    );
  return (
    <section className="panel inv-settings" aria-labelledby="inv-settings-title">
      <div className="panel-head">
        <h2 id="inv-settings-title">{t('inv.settings_title')}</h2>
      </div>
      <div className="form-stack">
        <div className="form-grid">
          {termList?.length || term ? (
            <Field label={t('terms.default')} hint={t('terms.default_hint')}>
              <TermSelect terms={termList} value={term} onChange={setTerm} noneLabel={t('terms.none_days')} />
            </Field>
          ) : null}
          <Field label={t('inv.default_days')} hint={t('inv.default_days_hint')} error={daysN === null ? t('inv.days_rule') : null}>
            <input className="input" inputMode="numeric" maxLength={3} value={days} onChange={(e) => setDays(e.target.value)} />
          </Field>
          <Field label={t('inv.quote_days')} hint={t('inv.quote_days_hint')} error={quoteDaysN === null ? t('inv.quote_days_rule') : null}>
            <input className="input" inputMode="numeric" maxLength={3} value={quoteDays} onChange={(e) => setQuoteDays(e.target.value)} />
          </Field>
        </div>
        <Field label={t('inv.terms')} hint={t('inv.terms_hint')} counter={`${terms.length}/1000`}>
          <textarea className="input textarea" rows={3} maxLength={1000} value={terms} onChange={(e) => setTerms(e.target.value)} />
        </Field>
        <h3>{t('inv.banks')}</h3>
        <p className="muted">{t('inv.banks_hint')}</p>
        {banks.map((b, i) => (
          <div key={i} className="inv-bank">
            <Field label={t('inv.bank')}>
              <input className="input" maxLength={80} value={b.bank} onChange={(e) => set(i, 'bank', e.target.value)} />
            </Field>
            <Field label="IBAN" error={b.iban && ibanBad(b.iban) ? t('inv.iban_rule') : null}>
              <input className="input mono" maxLength={42} value={b.iban} onChange={(e) => set(i, 'iban', e.target.value)} />
            </Field>
            <Field label="SWIFT">
              <input className="input mono" maxLength={11} value={b.swift} onChange={(e) => set(i, 'swift', e.target.value)} />
            </Field>
            <Field label={t('inv.currency')}>
              <input className="input mono" maxLength={3} value={b.currency} onChange={(e) => set(i, 'currency', e.target.value)} />
            </Field>
            <IconButton icon="trash" label={t('inv.remove_bank')} onClick={() => setBanks(banks.filter((_, j) => j !== i))} />
          </div>
        ))}
        <div className="row-actions">
          <Button icon="plus" onClick={() => setBanks([...banks, { bank: '', iban: '', swift: '', currency: 'ALL' }])} disabled={banks.length >= 10}>
            {t('inv.add_bank')}
          </Button>
        </div>
        <ReminderSettings levels={levels} onLevels={setLevels} auto={auto} onAuto={setAuto} />
        <div className="form-actions">
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={daysN === null || quoteDaysN === null || bad || levelsBad}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </section>
  );
}
